/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, HostListener, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { NumberValidatorService } from '../../../../shared/services/number-validator/number-validator.service';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LeadsCriteria,
  LeadsCriteriaValues,
  RangeValue,
  SUPPORTED_LANGUAGES,
  BUSINESS_SUPPORT_OPTIONS,
  BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME,
  LOAN_TYPE_OPTIONS,
  FINANCIAL_DOC_OPTIONS,
  CREDIT_SCORE_OPTIONS,
  AREA_TYPE_OPTIONS,
  BUSINESS_AGE_OPTIONS,
  INDUSTRY_OPTIONS,
  SUPPORT_AREA_OPTIONS,
  BUSINESS_HELP_AREA_OPTIONS,
  OPERATIONAL_TOOLS_OPTIONS,
} from '../../models/leads-criteria.model';
import { LeadsCriteriaService } from '../../services/leads-criteria.service';
import { takeUntil, forkJoin, Observable, of, tap, switchMap } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { getFieldRequirements } from '../../configs/leads-criteria-field-requirements.config';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { AddressService } from '../../../../shared/services/address/address.service';
import {
  State,
  County,
  City,
  Zipcode,
} from '../../../../shared/models/address';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';

@Component({
  selector: 'app-leads-criteria-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PopupComponent],
  templateUrl: './leads-criteria-detail.component.html',
  styleUrl: './leads-criteria-detail.component.scss',
})
export class LeadsCriteriaDetailComponent
  extends MenuComponentBase
  implements OnInit
{
  fb = inject(FormBuilder);
  leadsCriteriaService = inject(LeadsCriteriaService);
  authService = inject(AuthService);
  http = inject(HttpClient);
  override route = inject(ActivatedRoute);
  router = inject(Router);
  auditLogService = inject(AuditLogService);
  addressService = inject(AddressService);
  commonService = inject(CommonService);
  numberValidatorService = inject(NumberValidatorService);

  matchingCriteriaForm!: FormGroup;
  connectionName: string | undefined;
  criteriaId: string | null = null;
  private criteriaExists = false;
  isEditMode = false;
  savedCriteria: LeadsCriteria | null = null;
  isLoading = true;
  showBackConfirmPopup = false;
  private isUpdatingConditionalValidations = false;
  saveApiValidationMessages: string[] = [];

  // Mandatory match fields tracking
  mandatoryMatchFields: Set<string> = new Set<string>();

  // Counties data
  availableCounties: string[] = [];
  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  zipcodes: Zipcode[] = [];

  // Component-level cache for API responses
  private statesCache: State[] | null = null;
  private citiesAndCountiesCache = new Map<
    string,
    { counties: County[]; cities: City[] }
  >();
  private citiesAndCountiesByStateCache = new Map<
    number,
    { counties: County[]; cities: City[] }
  >();
  private zipcodesCache = new Map<number, Zipcode[]>();

  // Search properties
  stateSearchTerm = '';
  countySearchTerm = '';
  industrySearchTerm = '';
  citySearchTerm = '';
  zipSearchTerm = '';

  private cityZipcodeMap = new Map<number, number[]>();

  connection = this.authService.getLoggedInUserConnectionName();

  // Options from model constants
  readonly supportedLanguageOptions = [...SUPPORTED_LANGUAGES];
  readonly businessSupportOptions = [...BUSINESS_SUPPORT_OPTIONS];
  readonly loanTypeOptions = [...LOAN_TYPE_OPTIONS];
  readonly financialDocOptions = [...FINANCIAL_DOC_OPTIONS];
  readonly creditScoreOptions = [...CREDIT_SCORE_OPTIONS];
  readonly areaTypeOptions = [...AREA_TYPE_OPTIONS];
  readonly businessAgeOptions = [...BUSINESS_AGE_OPTIONS];
  readonly industryOptions = [...INDUSTRY_OPTIONS];
  readonly supportAreaOptions = [...SUPPORT_AREA_OPTIONS];
  readonly businessHelpAreaOptions = [...BUSINESS_HELP_AREA_OPTIONS];
  readonly operationalToolsOptions = [...OPERATIONAL_TOOLS_OPTIONS];

  // Loan type mapping: Display label -> API key
  private readonly loanTypeMapping: Record<string, string> = {
    'Term Loans': 'term_loan',
    'Working Capital Loans': 'working_capital_loan',
    'SBA Loan Product': 'sba_loan_product',
    'Loan to purchase or upgrade a business facility or equipment':
      'business_facility_or_equipment_loan',
    'Refinance other Debt': 'refinance_other_debt',
    'Real Estate Lending (residential)': 'real_estate_lending_residential',
    'Commercial Real Estate Purchase': 'commercial_real_estate_purchase',
    'Start-up Support': 'start_up_support',
    'Short-term quick capital': 'short_term_quick_capital',
    'Business Acquisition': 'business_acquisition',
    'Grant Programs': 'grant_programs',
    'Venture Funding (Debt, equity, or Revenue Share)': 'venture_funding',
    'Acquisition / M&A': 'acquisition_m_a',
    'Project Finance': 'project_finance',
  };

  // Reverse mapping: API key -> Display label
  private readonly loanTypeReverseMapping: Record<string, string> = {
    term_loan: 'Term Loans',
    working_capital_loan: 'Working Capital Loans',
    sba_loan_product: 'SBA Loan Product',
    business_facility_or_equipment_loan:
      'Loan to purchase or upgrade a business facility or equipment',
    refinance_other_debt: 'Refinance other Debt',
    real_estate_lending_residential: 'Real Estate Lending (residential)',
    commercial_real_estate_purchase: 'Commercial Real Estate Purchase',
    start_up_support: 'Start-up Support',
    short_term_quick_capital: 'Short-term quick capital',
    business_acquisition: 'Business Acquisition',
    grant_programs: 'Grant Programs',
    venture_funding: 'Venture Funding (Debt, equity, or Revenue Share)',
    acquisition_m_a: 'Acquisition / M&A',
    project_finance: 'Project Finance',
  };

  override ngOnInit(): void {
    // Set the menu to leads-criteria explicitly since this component handles detail/create routes
    const leadsMenuItem = this.menuService.getMenuItem('leads-criteria');
    if (leadsMenuItem) {
      this.menuService.menuOnChange(leadsMenuItem);
    }

    this.isLoading = true;
    this.connectionName = this.authService.getLoggedInUserConnectionName();
    this.criteriaId = this.route.snapshot.paramMap.get('id');
    this.initializeForm();

    // Determine if we're creating new criteria or loading existing by ID
    let criteriaData$: Observable<boolean>;

    if (this.criteriaId) {
      // We have an ID, so load specific criteria
      criteriaData$ = this.loadCriteriaById(this.criteriaId);
    } else {
      // No ID means this is a create flow
      criteriaData$ = this.loadForCreate();
    }

    // Wait for both operations to complete
    forkJoin([criteriaData$])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error: unknown) => {
          console.error('Error during initialization:', error);
          this.isLoading = false;
        },
      });
  }

  private loadStates(): Observable<boolean> {
    return this.addressService.getStates().pipe(
      takeUntil(this.destroy$),
      map((states) => {
        this.states = states;
        return true;
      }),
      catchError((error) => {
        console.error('Error loading states:', error);
        return of(false);
      }),
    );
  }

  // Cache helper methods
  private getCachedOrFetchStates(): Observable<State[]> {
    if (this.statesCache) {
      return of(this.statesCache);
    }
    return this.addressService.getStates().pipe(
      takeUntil(this.destroy$),
      tap((states) => {
        this.statesCache = states;
      }),
    );
  }

  private mergeCountiesAndCities(
    chunks: { counties: County[]; cities: City[] }[],
  ): { counties: County[]; cities: City[] } {
    const countyMap = new Map<number, County>();
    const cityMap = new Map<number, City>();

    chunks.forEach(({ counties, cities }) => {
      counties.forEach((county) => {
        if (!countyMap.has(county.id)) {
          countyMap.set(county.id, county);
        }
      });
      cities.forEach((city) => {
        if (!cityMap.has(city.id)) {
          cityMap.set(city.id, city);
        }
      });
    });

    return {
      counties: Array.from(countyMap.values()),
      cities: Array.from(cityMap.values()),
    };
  }

  private getCachedOrFetchCitiesAndCounties(
    stateIds: number[],
  ): Observable<{ counties: County[]; cities: City[] }> {
    const uniqueStateIds = Array.from(
      new Set(stateIds.filter(Number.isFinite)),
    );
    if (!uniqueStateIds.length) {
      return of({ counties: [], cities: [] });
    }

    const sortedStateIds = uniqueStateIds.sort((a, b) => a - b);
    const cacheKey = this.generateStateIdsCacheKey(sortedStateIds);
    const cached = this.citiesAndCountiesCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const cachedChunks = sortedStateIds
      .filter((id) => this.citiesAndCountiesByStateCache.has(id))
      .map((id) => this.citiesAndCountiesByStateCache.get(id)!);
    const missingStateIds = sortedStateIds.filter(
      (id) => !this.citiesAndCountiesByStateCache.has(id),
    );

    if (missingStateIds.length === 0) {
      const merged = this.mergeCountiesAndCities(cachedChunks);
      this.citiesAndCountiesCache.set(cacheKey, merged);
      return of(merged);
    }

    return this.addressService
      .getCitiesAndCountiesByStateIds(missingStateIds)
      .pipe(
        takeUntil(this.destroy$),
        tap((result) => {
          // store full response in combined cache
          this.citiesAndCountiesCache.set(cacheKey, result);

          missingStateIds.forEach((stateId) => {
            this.citiesAndCountiesByStateCache.set(stateId, result);
          });
        }),
        map((fetchedResult) => {
          const merged = this.mergeCountiesAndCities([
            ...cachedChunks,
            fetchedResult,
          ]);
          this.citiesAndCountiesCache.set(cacheKey, merged);
          return merged;
        }),
      );
  }

  private getCachedOrFetchZipcodes(cityIds: number[]): Observable<Zipcode[]> {
    // Separate cached and uncached city IDs
    const cachedZipcodes: Zipcode[] = [];
    const uncachedCityIds: number[] = [];

    cityIds.forEach((cityId) => {
      const cached = this.zipcodesCache.get(cityId);
      if (cached) {
        cachedZipcodes.push(...cached);
      } else {
        uncachedCityIds.push(cityId);
      }
    });

    // If all cities are cached, return combined cached zipcodes
    if (uncachedCityIds.length === 0) {
      return of(
        cachedZipcodes.filter(
          (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
        ),
      );
    }

    // Fetch zipcodes for uncached cities - make individual API calls to avoid passing cached city IDs
    const apiCalls = uncachedCityIds.map((cityId) =>
      this.addressService.getZipcodesByCityIds([cityId]).pipe(
        takeUntil(this.destroy$),
        tap((zipcodes) => {
          // Cache zipcodes per city
          this.zipcodesCache.set(cityId, zipcodes);
        }),
      ),
    );

    return forkJoin(apiCalls).pipe(
      takeUntil(this.destroy$),
      map((zipcodeArrays) => {
        const allZipcodes = zipcodeArrays.flat();
        // Combine with cached zipcodes
        const combined = [...cachedZipcodes, ...allZipcodes];
        return combined.filter(
          (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
        );
      }),
    );
  }

  private generateStateIdsCacheKey(stateIds: number[]): string {
    return `states_${stateIds.sort((a, b) => a - b).join('_')}`;
  }

  private generateCityIdsCacheKey(cityIds: number[]): string {
    return `cities_${cityIds.sort((a, b) => a - b).join('_')}`;
  }

  loadCities(): void {
    const selectedCounties = this.countiesOfServiceArray.value || [];
    if (!selectedCounties.length) {
      this.cities = [];
      this.zipcodes = [];
      this.citiesArray.clear();
      this.zipCodesArray.clear();
      // Note: Not marking as dirty here since this is a reactive cleanup, not user action
      return;
    }

    // If cities are not already loaded, fetch both counties and cities
    if (!this.cities.length) {
      this.loadCountiesAndCities();
      return;
    }

    // Remove any cities/zipcodes that are no longer valid
    this.cleanInvalidCitiesAndZipcodes();
  }

  private cleanInvalidCitiesAndZipcodes(): void {
    const validCityNames = this.cities.map((c) => c.name);

    // Remove invalid cities
    const citiesArray = this.citiesArray;
    let removed = false;
    for (let i = citiesArray.length - 1; i >= 0; i--) {
      const cityName = citiesArray.at(i).value;
      if (!validCityNames.includes(cityName)) {
        citiesArray.removeAt(i);
        removed = true;
      }
    }
    if (removed) {
      citiesArray.markAsDirty();
    }

    // Reload zipcodes for remaining cities
    this.reloadZipcodesForSelectedCities();
  }

  private loadCountiesAndCities(): void {
    const selectedStates = (this.statesOfServiceArray.value as string[]) || [];

    if (!selectedStates.length) {
      this.availableCounties = [];
      this.counties = [];
      this.cities = [];
      return;
    }

    const uniqueStateIds = Array.from(
      new Set(
        selectedStates
          .map((stateName) => this.states.find((s) => s.name === stateName))
          .filter((s): s is State => !!s)
          .map((s) => s.id),
      ),
    );

    if (!uniqueStateIds.length) {
      this.availableCounties = [];
      this.counties = [];
      this.cities = [];
      return;
    }

    this.getCachedOrFetchCitiesAndCounties(uniqueStateIds)
      .pipe(
        tap(({ counties, cities }) => {
          this.counties = counties;
          this.availableCounties = [...new Set(counties.map((c) => c.name))];

          this.cities = cities.filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          );

          this.cleanInvalidCitiesAndZipcodes();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private loadCriteriaById(id: string): Observable<boolean> {
    return this.leadsCriteriaService.getLeadCriteriaById(id).pipe(
      takeUntil(this.destroy$),
      map((criteria) => {
        this.criteriaExists = true;
        this.savedCriteria = criteria;
        this.isEditMode = false; // Start in view mode when loading specific criteria
        this.loadLeadsCriteria(criteria);
        return true;
      }),
      catchError((error) => {
        console.error('Error loading lead criteria by ID:', error);
        this.criteriaExists = false;
        this.isEditMode = true; // Start in edit mode on error
        if (error.status === 404) {
          this.router.navigate(['/deeproots/leads-criteria']);
          setTimeout(() => {
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: 'The requested matching criteria could not be found.',
            });
          }, 100);
        }
        return [false];
      }),
    );
  }

  private loadForCreate(): Observable<boolean> {
    // For create mode, we don't need to load any data
    // Just set the appropriate flags
    this.criteriaExists = false;
    this.savedCriteria = null;
    this.isEditMode = true; // Start in edit mode for creating new criteria
    this.mandatoryMatchFields.clear(); // Clear any mandatory fields
    return this.loadStates();
  }

  private initializeForm(): void {
    this.matchingCriteriaForm = this.fb.group(
      {
        name: ['', [Validators.required, this.noBlankValidator]],
        description: [''],
        supportedLanguages: this.fb.array(
          [],
          [Validators.required, this.atLeastOneSelectedValidator],
        ),
        nationwideSupport: ['', Validators.required],
        statesOfService: this.fb.array(
          [],
          [Validators.required, this.atLeastOneSelectedValidator],
        ),
        countiesOfService: this.fb.array(
          [],
          [Validators.required, this.atLeastOneSelectedValidator],
        ),
        cities: this.fb.array([]),
        zipCodes: this.fb.array([]),
        businessSupport: this.fb.array(
          [],
          [Validators.required, this.atLeastOneSelectedValidator],
        ),
        isMilitaryProgram: ['', Validators.required],
        isVeteranSpouse: ['', Validators.required],
        needBusinessEntity: ['', Validators.required],
        needStateRegistration: [''],
        hasTargetIndustries: [''],
        targetIndustries: this.fb.array([]),
        supportAreas: this.fb.array([]),
        offersEmployeeOwnership: [''],
        businessHelpAreas: this.fb.array([]),
        taProgramsPaidService: [''],
        paidSupportAreas: this.fb.array([]),
        operationalTools: this.fb.array([]),
        creHelpLocateFacilities: [''],
        creUpgradeFacility: [''],
        creNeedOwnFacility: [''],
        creWorkWithTenants: [''],
        creSolarEnergyUpgrades: [''],
        solarLendingProducts: [''],
        solarLoanMinAmount: [
          0,
          [this.numberValidatorService.numberValidator('solarLoanMinAmount')],
        ],
        solarLoanMaxAmount: [
          0,
          [this.numberValidatorService.numberValidator('solarLoanMaxAmount')],
        ],
        solarNeedCreditScore: [''],
        solarCreditScoreRange: [''],
        solarLoanRequirements: [''],
        offersEnergyEfficiency: [''],
        offersBatteryLending: [''],
        batteryLoanMinAmount: [
          0,
          [this.numberValidatorService.numberValidator('batteryLoanMinAmount')],
        ],
        batteryLoanMaxAmount: [
          0,
          [this.numberValidatorService.numberValidator('batteryLoanMaxAmount')],
        ],
        batteryNeedCreditScore: [''],
        batteryCreditScoreRange: [''],
        batteryLoanRequirements: [''],
        needPowerUseStatement: [''],
        offersEvProducts: [''],
        evLoanMinAmount: [
          0,
          [this.numberValidatorService.numberValidator('evLoanMinAmount')],
        ],
        evLoanMaxAmount: [
          0,
          [this.numberValidatorService.numberValidator('evLoanMaxAmount')],
        ],
        evNeedCreditScore: [''],
        evCreditScoreRange: [''],
        evLoanRequirements: [''],
        investmentTypes: this.fb.array([]),
        requireRegionalInvestment: [''],
        cosignerHelps: [''],
        needBusinessPlan: [''],
        needFinancialProjections: [''],
        loanTypes: this.fb.array([]),
        loanMinAmount: [
          0,
          [this.numberValidatorService.numberValidator('loanMinAmount')],
        ],
        loanMaxAmount: [
          0,
          [this.numberValidatorService.numberValidator('loanMaxAmount')],
        ],
        financialDocumentation: this.fb.array([]),
        creditScoreRange: [''],
        areaTypes: this.fb.array([]),
      },
      {
        validators:
          Validators.compose([
            this.loanAmountValidator,
            this.solarLoanAmountValidator,
            this.batteryLoanAmountValidator,
            this.evLoanAmountValidator,
          ]) ?? [],
      },
    );

    // Subscribe to CRE solar energy upgrades changes
    this.matchingCriteriaForm
      .get('creSolarEnergyUpgrades')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onCreSolarEnergyUpgradesChange(value);
      });

    // Subscribe to nationwide support changes to update location fields validation
    this.matchingCriteriaForm
      .get('nationwideSupport')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onNationwideSupportChange(value);
      });

    // Subscribe to business support changes for dynamic validation
    this.businessSupportArray.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    // Subscribe to Small Business Technical Assistance dynamic fields`
    this.matchingCriteriaForm
      .get('hasTargetIndustries')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.supportAreasArray.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('taProgramsPaidService')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    // Subscribe to solar/energy nested question changes for dynamic validation
    this.matchingCriteriaForm
      .get('solarLendingProducts')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('solarNeedCreditScore')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('offersBatteryLending')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('batteryNeedCreditScore')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('offersEvProducts')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    this.matchingCriteriaForm
      .get('evNeedCreditScore')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateConditionalValidations();
      });

    // Clear server-side validation messages once user edits the form
    this.matchingCriteriaForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.saveApiValidationMessages.length > 0) {
          this.saveApiValidationMessages = [];
        }
      });

    // Ensure dynamic validators are applied on first render
    this.updateConditionalValidations();
  }

  get supportedLanguagesArray(): FormArray {
    return this.matchingCriteriaForm.get('supportedLanguages') as FormArray;
  }

  get statesOfServiceArray(): FormArray {
    return this.matchingCriteriaForm.get('statesOfService') as FormArray;
  }

  get countiesOfServiceArray(): FormArray {
    return this.matchingCriteriaForm.get('countiesOfService') as FormArray;
  }

  get citiesArray(): FormArray {
    return this.matchingCriteriaForm.get('cities') as FormArray;
  }

  get zipCodesArray(): FormArray {
    return this.matchingCriteriaForm.get('zipCodes') as FormArray;
  }

  get businessSupportArray(): FormArray {
    return this.matchingCriteriaForm.get('businessSupport') as FormArray;
  }

  get loanTypesArray(): FormArray {
    return this.matchingCriteriaForm.get('loanTypes') as FormArray;
  }

  get financialDocArray(): FormArray {
    return this.matchingCriteriaForm.get('financialDocumentation') as FormArray;
  }

  get areaTypesArray(): FormArray {
    return this.matchingCriteriaForm.get('areaTypes') as FormArray;
  }

  get targetIndustriesArray(): FormArray {
    return this.matchingCriteriaForm.get('targetIndustries') as FormArray;
  }

  get supportAreasArray(): FormArray {
    return this.matchingCriteriaForm.get('supportAreas') as FormArray;
  }

  get businessHelpAreasArray(): FormArray {
    return this.matchingCriteriaForm.get('businessHelpAreas') as FormArray;
  }

  get paidSupportAreasArray(): FormArray {
    return this.matchingCriteriaForm.get('paidSupportAreas') as FormArray;
  }

  get operationalToolsArray(): FormArray {
    return this.matchingCriteriaForm.get('operationalTools') as FormArray;
  }

  get investmentTypesArray(): FormArray {
    return this.matchingCriteriaForm.get('investmentTypes') as FormArray;
  }

  // Filtered options for search functionality
  get filteredStates(): string[] {
    if (!this.stateSearchTerm) {
      return this.states.map((s) => s.name);
    }

    return this.states
      .map((s) => s.name)
      .filter((state) =>
        state.toLowerCase().includes(this.stateSearchTerm.toLowerCase()),
      );
  }

  get filteredCounties(): string[] {
    if (!this.countySearchTerm) {
      return this.availableCounties;
    }
    return this.availableCounties.filter((county) =>
      county.toLowerCase().includes(this.countySearchTerm.toLowerCase()),
    );
  }

  get filteredIndustries() {
    if (!this.industrySearchTerm) {
      return this.industryOptions;
    }
    return this.industryOptions.filter((industry) =>
      industry.label
        .toLowerCase()
        .includes(this.industrySearchTerm.toLowerCase()),
    );
  }

  get filteredCities(): string[] {
    const cityNames = this.cities.map((c) => c.name);

    if (!this.citySearchTerm) {
      return cityNames;
    }

    return cityNames.filter((city) =>
      city.toLowerCase().includes(this.citySearchTerm.toLowerCase()),
    );
  }

  get filteredZipcodes(): string[] {
    const zipCodes = this.zipcodes.map((z) => z.code);

    if (!this.zipSearchTerm) {
      return zipCodes;
    }

    return zipCodes.filter((zip) =>
      zip.toLowerCase().includes(this.zipSearchTerm.toLowerCase()),
    );
  }

  onCitySearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.citySearchTerm = target.value;
  }

  onZipSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.zipSearchTerm = target.value;
  }

  clearCitySearch(): void {
    this.citySearchTerm = '';
  }

  clearZipSearch(): void {
    this.zipSearchTerm = '';
  }

  onIndustrySearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.industrySearchTerm = input.value;
  }

  clearIndustrySearch(): void {
    this.industrySearchTerm = '';
  }

  onStateToggle(stateName: string): void {
    const statesArray = this.statesOfServiceArray;
    const index = statesArray.value.indexOf(stateName);

    if (index > -1) {
      statesArray.removeAt(index);
      statesArray.markAsDirty();

      const state = this.states.find((s) => s.name === stateName);
      if (!state) return;

      // Remove counties belonging to this state from the FormArray
      const countiesToRemove = this.counties
        .filter((c) => c.id === state.id)
        .map((c) => c.name);

      const countiesArray = this.countiesOfServiceArray;

      for (let i = countiesArray.length - 1; i >= 0; i--) {
        const countyName = countiesArray.at(i).value;
        if (countiesToRemove.includes(countyName)) {
          countiesArray.removeAt(i);
        }
      }
      if (countiesArray.length > 0) {
        countiesArray.markAsDirty();
      }

      if (this.statesOfServiceArray.length === 0) {
        this.zipcodes = [];
        this.cities = [];
        this.zipCodesArray.clear();
        this.citiesArray.clear();

        // Force remove the "Must Match" status for these fields
        this.mandatoryMatchFields.delete('zip_codes');
        this.mandatoryMatchFields.delete('cities');
        this.mandatoryMatchFields.delete('counties');
      }

      const remainingZipCodes = this.zipcodes.map((z) => z.code);

      for (let i = this.zipCodesArray.length - 1; i >= 0; i--) {
        if (!remainingZipCodes.includes(this.zipCodesArray.at(i).value)) {
          this.zipCodesArray.removeAt(i);
        }
      }
      if (this.zipCodesArray.length > 0) {
        this.zipCodesArray.markAsDirty();
      }
    } else {
      statesArray.push(this.fb.control(stateName));
      statesArray.markAsDirty();
    }

    // reload counties + cities for selected states
    this.loadCountiesAndCities();
  }

  onCityToggle(cityName: string): void {
    const citiesArray = this.citiesArray;
    const index = citiesArray.value.indexOf(cityName);

    if (index > -1) {
      citiesArray.removeAt(index);
      citiesArray.markAsDirty();
    } else {
      citiesArray.push(this.fb.control(cityName));
      citiesArray.markAsDirty();
    }

    // Reload zipcodes for all selected cities
    this.reloadZipcodesForSelectedCities();
  }

  reloadZipcodesForSelectedCities(): void {
    const selectedCities = Array.from(
      new Set((this.citiesArray.value as string[]) || []),
    );

    const cityIds = selectedCities
      .map((cityName) => this.cities.find((c) => c.name === cityName))
      .filter((c): c is City => !!c)
      .map((c) => c.id);

    if (!cityIds.length) {
      this.zipcodes = [];
      this.zipCodesArray.clear();
      this.zipCodesArray.markAsDirty();
      return;
    }

    this.getCachedOrFetchZipcodes(cityIds)
      .pipe(
        tap((zipcodes) => {
          this.zipcodes = zipcodes.filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          );

          const validZipCodes = this.zipcodes.map((z) => z.code);
          const zipFormArray = this.zipCodesArray;
          let removed = false;
          for (let i = zipFormArray.length - 1; i >= 0; i--) {
            if (!validZipCodes.includes(zipFormArray.at(i).value)) {
              zipFormArray.removeAt(i);
              removed = true;
            }
          }
          if (removed) {
            zipFormArray.markAsDirty();
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  isStateSelected(state: string): boolean {
    return this.statesOfServiceArray.value.includes(state);
  }

  isNationwideSupport(): boolean {
    return this.matchingCriteriaForm.get('nationwideSupport')?.value === 'yes';
  }

  // Select All functionality for Counties
  areAllCountiesSelected(): boolean {
    return (
      this.availableCounties.length > 0 &&
      this.countiesOfServiceArray.value.length === this.availableCounties.length
    );
  }

  areSomeCountiesSelected(): boolean {
    return this.countiesOfServiceArray.value.length > 0;
  }

  onSelectAllCounties(event: Event): void {
    const target = event.target as HTMLInputElement;
    const countiesArray = this.countiesOfServiceArray;

    if (target.checked) {
      // Select all counties
      countiesArray.clear();
      this.availableCounties.forEach((county) => {
        countiesArray.push(this.fb.control(county));
      });
      countiesArray.markAsDirty();
    } else {
      // Deselect all counties
      countiesArray.clear();
      countiesArray.markAsDirty();
      this.cities = [];
      this.zipcodes = [];
      this.citiesArray.clear();
      this.zipCodesArray.clear();
    }
    const remainingCounties =
      this.matchingCriteriaForm.get('countiesOfService')?.value || [];
    if (remainingCounties.length > 0) {
      this.loadCities();
    }

    // Mark as touched for validation
    countiesArray.markAsTouched();
  }

  // Generic method to toggle checkbox options
  onCheckboxToggle(arrayName: string, option: string): void {
    const array = this.matchingCriteriaForm.get(arrayName) as FormArray;
    const index = array.value.indexOf(option);

    if (index > -1) {
      // Removing the option
      array.removeAt(index);
      // Mark the FormArray as dirty so change detection works
      array.markAsDirty();

      // Handle unchecking Solar and Energy Efficiency Products
      if (
        arrayName === 'businessSupport' &&
        option === 'Solar and Energy Efficiency Products'
      ) {
        // Get the current business support values after removal
        const businessSupportValues = this.businessSupportArray.value;
        const creOption = 'Commercial Real Estate';

        // If Commercial Real Estate is still selected, set creSolarEnergyUpgrades to 'no'
        if (businessSupportValues.includes(creOption)) {
          this.matchingCriteriaForm.patchValue({
            creSolarEnergyUpgrades: 'no',
          });
        }
      }

      // Clear related form values when unchecking a business support section
      if (arrayName === 'businessSupport') {
        this.clearSectionFormValues(option);
      }

      // Check if we need to auto-uncheck must match for this field
      const fieldMapping: Record<string, string> = {
        supportedLanguages: 'preferred_languages',
        statesOfService: 'states',
        countiesOfService: 'counties',
        businessSupport: 'supports',
      };
      const fieldName = fieldMapping[arrayName];
      if (fieldName) {
        this.checkAndClearMandatoryMatch(fieldName);
      }

      // Handle counties removal logic
      if (arrayName === 'countiesOfService') {
        const remainingCounties =
          this.matchingCriteriaForm.get('countiesOfService')?.value || [];

        if (remainingCounties.length > 0) {
          // Reload cities for remaining counties
          this.loadCities();
        } else {
          // Clear cities & zipcodes if no counties left
          this.cities = [];
          this.zipcodes = [];
          this.citiesArray.clear();
          this.zipCodesArray.clear();
        }
      }
    } else {
      array.push(this.fb.control(option));
      // Mark the FormArray as dirty so change detection works
      array.markAsDirty();

      // Handle adding Solar and Energy Efficiency Products
      if (
        arrayName === 'businessSupport' &&
        option === 'Solar and Energy Efficiency Products'
      ) {
        // Ensure Commercial Real Estate is also selected
        const creOption = 'Commercial Real Estate';
        const businessSupportValues = this.businessSupportArray.value;
        if (businessSupportValues.includes(creOption)) {
          // Set creSolarEnergyUpgrades to 'yes'
          this.matchingCriteriaForm.patchValue({
            creSolarEnergyUpgrades: 'yes',
          });
        }
      }

      // Handle counties addition
      if (arrayName === 'countiesOfService') {
        this.loadCities();
      }
    }
  }

  private clearSectionFormValues(sectionOption: string): void {
    switch (sectionOption) {
      case 'Commercial Real Estate':
        this.matchingCriteriaForm.patchValue({
          creHelpLocateFacilities: '',
          creUpgradeFacility: '',
          creNeedOwnFacility: '',
          creWorkWithTenants: '',
          creSolarEnergyUpgrades: '',
        });
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('commercial_real_estate');
        break;

      case 'Solar and Energy Efficiency Products':
        this.matchingCriteriaForm.patchValue({
          creSolarEnergyUpgrades: '',
          solarLendingProducts: '',
          solarLoanMinAmount: 0,
          solarLoanMaxAmount: 0,
          solarNeedCreditScore: '',
          solarCreditScoreRange: '',
          solarLoanRequirements: '',
          offersEnergyEfficiency: '',
          offersBatteryLending: '',
          batteryLoanMinAmount: 0,
          batteryLoanMaxAmount: 0,
          batteryNeedCreditScore: '',
          batteryCreditScoreRange: '',
          batteryLoanRequirements: '',
          needPowerUseStatement: '',
          offersEvProducts: '',
          evLoanMinAmount: 0,
          evLoanMaxAmount: 0,
          evNeedCreditScore: '',
          evCreditScoreRange: '',
          evLoanRequirements: '',
        });
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('solar_and_energy');
        break;

      case 'Loans and Capital Products':
        this.matchingCriteriaForm.patchValue({
          needStateRegistration: '',
          loanMinAmount: 0,
          loanMaxAmount: 0,
          cosignerHelps: '',
          needBusinessPlan: '',
          needFinancialProjections: '',
          creditScoreRange: '',
        });
        this.clearFormArray('loanTypes');
        this.clearFormArray('financialDocumentation');
        this.clearFormArray('areaTypes');
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('loans_and_capital');
        break;

      case 'Small Business Technical Assistance':
        this.matchingCriteriaForm.patchValue({
          hasTargetIndustries: '',
          offersEmployeeOwnership: '',
          taProgramsPaidService: '',
        });
        this.clearFormArray('targetIndustries');
        this.clearFormArray('supportAreas');
        this.clearFormArray('businessHelpAreas');
        this.clearFormArray('paidSupportAreas');
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('technical_assistance');
        break;

      case 'Business Operational Tools':
        this.clearFormArray('operationalTools');
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('business_operational_tools');
        break;

      case 'Investing in Companies':
        this.matchingCriteriaForm.patchValue({
          requireRegionalInvestment: '',
        });
        this.clearFormArray('investmentTypes');
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('finding_investors');
        break;

      case 'Chamber of Commerce Membership or Regional Networking Group':
        // No form fields to clear for this option
        // Clear mandatory match fields for this section
        this.clearSectionMandatoryFields('chamber_or_networking');
        break;
    }
  }

  private clearSectionMandatoryFields(sectionPrefix: string): void {
    // Remove all mandatory match fields that start with this section prefix
    const fieldsToRemove = Array.from(this.mandatoryMatchFields).filter(
      (field) => field.startsWith(sectionPrefix + '.'),
    );
    fieldsToRemove.forEach((field) => this.mandatoryMatchFields.delete(field));
  }

  private clearFormArray(arrayName: string): void {
    const array = this.matchingCriteriaForm.get(arrayName) as FormArray;
    if (array) {
      array.clear();
      array.markAsDirty();
    }
  }

  // Generic method to check if option is selected
  isOptionSelected(arrayName: string, option: string): boolean {
    const array = this.matchingCriteriaForm.get(arrayName) as FormArray;
    return array.value.includes(option);
  }

  // Search methods
  onStateSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.stateSearchTerm = target.value;
  }

  onCountySearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.countySearchTerm = target.value;
  }

  clearStateSearch(): void {
    this.stateSearchTerm = '';
  }

  clearCountySearch(): void {
    this.countySearchTerm = '';
  }

  addCity(): void {
    // Don't allow adding new city if there's already an empty one
    if (this.hasEmptyInput(this.citiesArray)) {
      return;
    }
    this.citiesArray.push(this.fb.control(''));
    this.citiesArray.markAsDirty();
  }

  removeCity(index: number): void {
    this.citiesArray.removeAt(index);
    this.citiesArray.markAsDirty();
    // Check if we need to auto-uncheck must match when all cities are removed
    this.checkAndClearMandatoryMatch('cities');
  }

  addZipCode(): void {
    // Don't allow adding new zip code if there's already an empty one
    if (this.hasEmptyInput(this.zipCodesArray)) {
      return;
    }
    this.zipCodesArray.push(this.fb.control('', [this.zipCodeValidator]));
    this.zipCodesArray.markAsDirty();
  }

  removeZipCode(index: number): void {
    this.zipCodesArray.removeAt(index);
    this.zipCodesArray.markAsDirty();
    // Check if we need to auto-uncheck must match when all zip codes are removed
    this.checkAndClearMandatoryMatch('zip_codes');
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      const firstError = document.querySelector(
        '.error-message',
      ) as HTMLElement;

      if (firstError) {
        firstError.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    });
  }

  onSave(): void {
    // First, clean up any empty entries
    this.cleanupEmptyEntries();
    this.saveApiValidationMessages = [];

    if (this.matchingCriteriaForm.valid) {
      const isCreate = !(this.criteriaExists && this.savedCriteria);

      // For create, always use full payload
      // For update, only send changed fields
      const leadsCriteria = isCreate
        ? this.getLeadsCriteriaFromForm()
        : this.getChangedFields();

      this.auditLogService.logFeature({
        featureName: 'leads_criteria',
        action: isCreate ? 'create' : 'update',
        meta: {
          criteriaId: this.savedCriteria?.id
            ? String(this.savedCriteria.id)
            : '',
          criteriaName: leadsCriteria.name || '',
        },
        description: isCreate
          ? 'User creates leads criteria'
          : 'User updates leads criteria',
      });

      // Use update if criteria exists, create if it doesn't
      const saveOperation = isCreate
        ? this.leadsCriteriaService.createLeadCriteria(
            leadsCriteria as LeadsCriteria,
          )
        : this.leadsCriteriaService.updateLeadCriteria(
            this.savedCriteria!.id.toString(),
            leadsCriteria as Partial<LeadsCriteria>,
          );

      saveOperation.pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          this.saveApiValidationMessages = [];
          this.matchingCriteriaForm.markAsPristine();
          // Set criteriaExists to true after successful creation/update
          this.criteriaExists = true;
          // Update saved criteria and switch to view mode
          this.savedCriteria = result;
          this.isEditMode = false;

          // Show success toast
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Lead criteria saved successfully!',
          });

          // Scroll to top of the page
          window.scrollTo({ top: 0, behavior: 'auto' });

          // If this was a create operation, navigate to the detail page with the new ID
          if (!this.criteriaId) {
            this.router.navigate([
              '/deeproots/leads-criteria/details',
              result.id,
            ]);
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error saving lead criteria:', error);
          if (error.status === 400) {
            const validationMessages = this.extractValidationMessagesFromError(
              error.error,
            );

            if (validationMessages.length > 0) {
              this.saveApiValidationMessages = validationMessages;
              this.commonService.triggerToast({
                type: ToastType.Error,
                message:
                  'Please review the validation errors shown above Save.',
              });
            } else {
              this.commonService.triggerToast({
                type: ToastType.Error,
                message:
                  'Please check you have filled out all required fields correctly.',
              });
            }
          } else if (error.status === 409) {
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: 'A lead criteria with the same name already exists.',
            });
          } else {
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: 'Failed to save lead criteria. Please try again.',
            });
          }
        },
      });
    } else {
      this.saveApiValidationMessages = [];
      this.markFormGroupTouched();
      this.scrollToFirstError();
    }
  }

  private extractValidationMessagesFromError(errorBody: unknown): string[] {
    if (!errorBody || typeof errorBody !== 'object') {
      return [];
    }

    const messages = new Set<string>();
    this.collectValidationMessages(errorBody, '', messages);
    return Array.from(messages);
  }

  private collectValidationMessages(
    value: unknown,
    path: string,
    messages: Set<string>,
  ): void {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (typeof entry === 'string') {
          const messagePrefix = path
            ? `${this.formatValidationPath(path)}: `
            : '';
          messages.add(`${messagePrefix}${entry}`);
        } else {
          this.collectValidationMessages(entry, path, messages);
        }
      });
      return;
    }

    if (!value || typeof value !== 'object') {
      return;
    }

    Object.entries(value as Record<string, unknown>).forEach(
      ([key, nestedValue]) => {
        if (key === 'errorId') {
          return;
        }

        const nextPath = path ? `${path}.${key}` : key;
        this.collectValidationMessages(nestedValue, nextPath, messages);
      },
    );
  }

  private formatValidationPath(path: string): string {
    const cleanedPath = path.startsWith('criteria.')
      ? path.replace('criteria.', '')
      : path;

    return cleanedPath
      .split('.')
      .map((segment) => segment.replace(/_/g, ' '))
      .join(' -> ');
  }

  /**
   * Get only the fields that have changed from the saved criteria.
   * Used for PATCH operations to minimize payload size.
   */
  private getChangedFields(): Partial<LeadsCriteria> {
    const payload: any = {};
    const formValue = this.matchingCriteriaForm.value;

    // Check if name changed
    if (formValue.name !== this.savedCriteria?.name) {
      payload.name = formValue.name;
    }

    // Check if description changed
    if (formValue.description !== this.savedCriteria?.description) {
      payload.description = formValue.description || null;
    }

    // Check if any criteria fields have changed
    // We do this by checking if the form controls related to criteria are dirty
    const criteriaChanged = this.hasCriteriaFieldsChanged();

    if (criteriaChanged) {
      // If criteria changed, include the full criteria object
      const fullCriteria = this.getLeadsCriteriaFromForm();
      payload.criteria = fullCriteria.criteria;
    }

    return payload as Partial<LeadsCriteria>;
  }

  /**
   * Check if any criteria-related form fields have been modified.
   */
  private hasCriteriaFieldsChanged(): boolean {
    // List of all criteria-related form control names
    const criteriaControls = [
      'supportedLanguages',
      'nationwideSupport',
      'statesOfService',
      'countiesOfService',
      'cities',
      'zipCodes',
      'businessSupport',
      'isMilitaryProgram',
      'isVeteranSpouse',
      'needBusinessEntity',
      'needStateRegistration',
      'loanTypes',
      'loanMinAmount',
      'loanMaxAmount',
      'financialDocumentation',
      'creditScoreRange',
      'areaTypes',
      'cosignerHelps',
      'needBusinessPlan',
      'needFinancialProjections',
      'hasTargetIndustries',
      'targetIndustries',
      'supportAreas',
      'offersEmployeeOwnership',
      'businessHelpAreas',
      'taProgramsPaidService',
      'paidSupportAreas',
      'operationalTools',
      'creHelpLocateFacilities',
      'creUpgradeFacility',
      'creNeedOwnFacility',
      'creWorkWithTenants',
      'creSolarEnergyUpgrades',
      'solarLendingProducts',
      'solarLoanMinAmount',
      'solarLoanMaxAmount',
      'solarNeedCreditScore',
      'solarCreditScoreRange',
      'solarLoanRequirements',
      'offersEnergyEfficiency',
      'offersBatteryLending',
      'batteryLoanMinAmount',
      'batteryLoanMaxAmount',
      'batteryNeedCreditScore',
      'batteryCreditScoreRange',
      'batteryLoanRequirements',
      'needPowerUseStatement',
      'offersEvProducts',
      'evLoanMinAmount',
      'evLoanMaxAmount',
      'evNeedCreditScore',
      'evCreditScoreRange',
      'evLoanRequirements',
      'investmentTypes',
      'requireRegionalInvestment',
    ];

    // Check if any criteria control is dirty or if mandatory match fields changed
    const hasDirtyControls = criteriaControls.some(
      (controlName) => this.matchingCriteriaForm.get(controlName)?.dirty,
    );

    // Check if mandatory match fields changed
    const mandatoryFieldsChanged = this.hasMandatoryFieldsChanged();

    return hasDirtyControls || mandatoryFieldsChanged;
  }

  /**
   * Check if mandatory match fields have changed.
   */
  private hasMandatoryFieldsChanged(): boolean {
    const savedMandatoryFields = new Set(
      this.savedCriteria?.criteria?.mandatory_match_fields || [],
    );

    // Check if sets are different
    if (savedMandatoryFields.size !== this.mandatoryMatchFields.size) {
      return true;
    }

    for (const field of this.mandatoryMatchFields) {
      if (!savedMandatoryFields.has(field)) {
        return true;
      }
    }

    return false;
  }

  private getLeadsCriteriaFromForm(): Partial<LeadsCriteria> {
    const formValue = this.matchingCriteriaForm.value;

    // Filter out empty cities array
    const cities = formValue.cities || [];
    const filteredCities = cities.filter(
      (city: string) => city && city.trim() !== '',
    );

    // Filter out empty zip codes array
    const zipCodes = formValue.zipCodes || [];
    const filteredZipCodes = zipCodes.filter(
      (zip: string) => zip && zip.trim() !== '',
    );

    const loanSizeRange: RangeValue = {
      min: Number(formValue.loanMinAmount) || 0,
      max: Number(formValue.loanMaxAmount) || 0,
    };

    // Convert business support array to lowercase underscore format
    const businessSupportArray = formValue.businessSupport || [];
    const supportsLowercase: string[] = [];
    if (businessSupportArray.includes('Loans and Capital Products'))
      supportsLowercase.push('loans_and_capital');
    if (businessSupportArray.includes('Small Business Technical Assistance'))
      supportsLowercase.push('technical_assistance');
    if (businessSupportArray.includes('Business Operational Tools'))
      supportsLowercase.push('business_operational_tools');
    if (businessSupportArray.includes('Commercial Real Estate'))
      supportsLowercase.push('commercial_real_estate');
    if (businessSupportArray.includes('Solar and Energy Efficiency Products'))
      supportsLowercase.push('solar_and_energy');
    if (businessSupportArray.includes('Investing in Companies'))
      supportsLowercase.push('finding_investors');
    if (
      businessSupportArray.includes(
        'Chamber of Commerce Membership or Regional Networking Group',
      )
    )
      supportsLowercase.push('chamber_or_networking');

    // Convert preferred languages array to lowercase values
    const languagesArray = formValue.supportedLanguages || [];
    const languagesLowercase = languagesArray.map((lang: string) =>
      lang.toLowerCase(),
    );

    // Convert support areas array to lowercase format
    const supportAreasArray = formValue.supportAreas || [];
    const supportAreasLowercase: string[] = [];
    if (supportAreasArray.includes('Marketing'))
      supportAreasLowercase.push('marketing');
    if (supportAreasArray.includes('Finances/Accounting'))
      supportAreasLowercase.push('finances_accounting');
    if (supportAreasArray.includes('Strategy'))
      supportAreasLowercase.push('strategy');
    if (supportAreasArray.includes('Legal Assistance/Questions'))
      supportAreasLowercase.push('legal_assistance');
    if (supportAreasArray.includes('Business Succession Planning'))
      supportAreasLowercase.push('business_succession');
    if (supportAreasArray.includes('Applying for a Grant'))
      supportAreasLowercase.push('applying_for_grant');
    if (supportAreasArray.includes('Tax Structure/Tax support'))
      supportAreasLowercase.push('tax_structure');
    if (supportAreasArray.includes('Site Selection'))
      supportAreasLowercase.push('site_selection');
    if (supportAreasArray.includes('Human Resources'))
      supportAreasLowercase.push('human_resources');
    if (supportAreasArray.includes('Operational Planning'))
      supportAreasLowercase.push('operational_planning');
    if (supportAreasArray.includes('Applying for a loan or capital'))
      supportAreasLowercase.push('applying_for_loan');
    if (supportAreasArray.includes('Banking Accounts/Systems'))
      supportAreasLowercase.push('banking_accounts');
    if (supportAreasArray.includes('Technology'))
      supportAreasLowercase.push('technology');
    if (supportAreasArray.includes('IT Infrastructure'))
      supportAreasLowercase.push('it_infrastructure');
    if (supportAreasArray.includes('Investment Readiness Support'))
      supportAreasLowercase.push('investment_readiness');
    if (supportAreasArray.includes('Government Contracting'))
      supportAreasLowercase.push('government_contracting');
    if (supportAreasArray.includes('Manufacturing Support'))
      supportAreasLowercase.push('manufacturing_support');

    // Build criteriaValues conditionally based on selected sections
    const criteriaValues: Partial<LeadsCriteriaValues> = {
      preferred_languages: languagesLowercase,
      nationwide_support: formValue.nationwideSupport === 'yes',
      supports: supportsLowercase,
      need_business_entity: formValue.needBusinessEntity === 'yes',
      is_military_veteran_prog: formValue.isMilitaryProgram === 'yes',
      is_veteran_spouse_prog: formValue.isVeteranSpouse === 'yes',
    };

    // Only include location fields if nationwide support is not enabled
    if (formValue.nationwideSupport !== 'yes') {
      criteriaValues.states = formValue.statesOfService || [];
      criteriaValues.counties = formValue.countiesOfService || [];
    }

    // Only include Loans and Capital related fields if selected
    if (supportsLowercase.includes('loans_and_capital')) {
      const financialDocsLowercase: string[] = [];
      if (
        formValue.financialDocumentation?.includes(
          'Audited financial statements',
        )
      )
        financialDocsLowercase.push('audited_statements');
      if (
        formValue.financialDocumentation?.includes(
          'Unaudited formal financial statements',
        )
      )
        financialDocsLowercase.push('unaudited_statements');
      if (
        formValue.financialDocumentation?.includes(
          'Informal financial statements',
        )
      )
        financialDocsLowercase.push('informal_statements');
      if (formValue.financialDocumentation?.includes('Tax returns'))
        financialDocsLowercase.push('tax_returns');
      if (
        formValue.financialDocumentation?.includes(
          'No financial statements available',
        )
      )
        financialDocsLowercase.push('no_statements');
      if (formValue.financialDocumentation?.includes('Bank Statements'))
        financialDocsLowercase.push('bank_statements');

      // Convert selected loan types from display labels to API keys
      const selectedLoanTypes: string[] = (formValue.loanTypes || [])
        .map((label: string) => this.loanTypeMapping[label])
        .filter((key: string) => key !== undefined);

      (criteriaValues as any).loans_and_capital = {
        need_state_registration: formValue.needStateRegistration === 'yes',
        cosigner_helps: formValue.cosignerHelps || '',
        need_business_plan: formValue.needBusinessPlan || '',
        need_financial_projections: formValue.needFinancialProjections || '',
        loan_types: selectedLoanTypes,
        loan_size_range: loanSizeRange,
        financial_documentations: financialDocsLowercase,
        credit_score_range: this.convertCreditScoreToApiSingle(
          formValue.creditScoreRange || '',
        ),
        capital_deployment_areas: this.convertCapitalDeploymentAreasToApi(
          formValue.areaTypes || [],
        ),
      };
    }

    // Only include Technical Assistance related fields if selected
    if (supportsLowercase.includes('technical_assistance')) {
      const businessHelpAreasLowercase: string[] = [];
      if (formValue.businessHelpAreas?.includes('Creating a Business Plan'))
        businessHelpAreasLowercase.push('business_plan');
      if (
        formValue.businessHelpAreas?.includes('Creating a Financial Forecast')
      )
        businessHelpAreasLowercase.push('financial_forecast');
      if (
        formValue.businessHelpAreas?.includes(
          'Understanding Business Structures',
        )
      )
        businessHelpAreasLowercase.push('business_structures');
      if (
        formValue.businessHelpAreas?.includes(
          'Knowing what resources are available to support you',
        )
      )
        businessHelpAreasLowercase.push('available_resources');

      const paidSupportAreasLowercase: string[] = [];
      if (formValue.paidSupportAreas?.includes('Marketing'))
        paidSupportAreasLowercase.push('marketing');
      if (formValue.paidSupportAreas?.includes('Finances/Accounting'))
        paidSupportAreasLowercase.push('finances_accounting');
      if (formValue.paidSupportAreas?.includes('Strategy'))
        paidSupportAreasLowercase.push('strategy');
      if (formValue.paidSupportAreas?.includes('Legal Assistance/Questions'))
        paidSupportAreasLowercase.push('legal_assistance');
      if (formValue.paidSupportAreas?.includes('Business Succession Planning'))
        paidSupportAreasLowercase.push('business_succession');
      if (formValue.paidSupportAreas?.includes('Applying for a Grant'))
        paidSupportAreasLowercase.push('applying_for_grant');
      if (formValue.paidSupportAreas?.includes('Tax Structure/Tax support'))
        paidSupportAreasLowercase.push('tax_structure');
      if (formValue.paidSupportAreas?.includes('Site Selection'))
        paidSupportAreasLowercase.push('site_selection');
      if (formValue.paidSupportAreas?.includes('Human Resources'))
        paidSupportAreasLowercase.push('human_resources');
      if (formValue.paidSupportAreas?.includes('Operational Planning'))
        paidSupportAreasLowercase.push('operational_planning');
      if (
        formValue.paidSupportAreas?.includes('Applying for a loan or capital')
      )
        paidSupportAreasLowercase.push('applying_for_loan');
      if (formValue.paidSupportAreas?.includes('Banking Accounts/Systems'))
        paidSupportAreasLowercase.push('banking_accounts');
      if (formValue.paidSupportAreas?.includes('Technology'))
        paidSupportAreasLowercase.push('technology');
      if (formValue.paidSupportAreas?.includes('IT Infrastructure'))
        paidSupportAreasLowercase.push('it_infrastructure');
      if (formValue.paidSupportAreas?.includes('Investment Readiness Support'))
        paidSupportAreasLowercase.push('investment_readiness');
      if (formValue.paidSupportAreas?.includes('Government Contracting'))
        paidSupportAreasLowercase.push('government_contracting');
      if (formValue.paidSupportAreas?.includes('Manufacturing Support'))
        paidSupportAreasLowercase.push('manufacturing_support');

      (criteriaValues as any).technical_assistance = {
        has_target_industries: formValue.hasTargetIndustries === 'yes',
        target_industries: formValue.targetIndustries || [],
        support_areas: supportAreasLowercase,
        offers_employee_ownership: formValue.offersEmployeeOwnership === 'yes',
        business_help_areas: businessHelpAreasLowercase,
        ta_programs_paid_service: formValue.taProgramsPaidService || '',
        paid_support_areas: paidSupportAreasLowercase,
      };
    }

    // Only include Operational Tools related fields if selected
    if (supportsLowercase.includes('business_operational_tools')) {
      const businessBankingTools: string[] = [];
      const marketingHelpTools: string[] = [];
      const techSupportTools: string[] = [];
      const businessInsuranceTools: string[] = [];
      const newTechnologyTools: string[] = [];

      // Business Banking
      if (
        formValue.operationalTools?.includes(
          'Open or manage business accounts (checking, savings, investments)',
        )
      )
        businessBankingTools.push('business_accounts');
      if (formValue.operationalTools?.includes('Help with payroll and HR'))
        businessBankingTools.push('payroll_and_hr');
      if (formValue.operationalTools?.includes('Get a line of credit'))
        businessBankingTools.push('lines_of_credit');
      if (
        formValue.operationalTools?.includes(
          "Help managing your company's cash flow",
        )
      )
        businessBankingTools.push('manage_cash_flow');
      if (formValue.operationalTools?.includes('Advice on financial strategy'))
        businessBankingTools.push('financial_strategy');

      // Marketing Help
      if (
        formValue.operationalTools?.includes(
          'Help designing your website or improving search engine visibility',
        )
      )
        marketingHelpTools.push('website_design');
      if (
        formValue.operationalTools?.includes(
          'Help with flyers, signs, or other physical marketing',
        )
      )
        marketingHelpTools.push('physical_marketing');
      if (
        formValue.operationalTools?.includes(
          'Help planning or improving your marketing strategy',
        )
      )
        marketingHelpTools.push('marketing_strategy');

      // Tech Support
      if (
        formValue.operationalTools?.includes(
          'Help with IT setup, troubleshooting, and online security',
        )
      )
        techSupportTools.push('it_support');
      if (
        formValue.operationalTools?.includes(
          'Help setting up physical tech like servers, internet, etc.',
        )
      )
        techSupportTools.push('physical_infrastructure');

      // Business Insurance
      if (formValue.operationalTools?.includes('General business insurance'))
        businessInsuranceTools.push('general_insurance');
      if (
        formValue.operationalTools?.includes(
          'Insurance for disasters (flood, fire, etc.)',
        )
      )
        businessInsuranceTools.push('disaster_insurance');
      if (
        formValue.operationalTools?.includes('Property or building insurance')
      )
        businessInsuranceTools.push('building_insurance');
      if (formValue.operationalTools?.includes('Protection from lawsuits'))
        businessInsuranceTools.push('lawsuit_protection');
      if (
        formValue.operationalTools?.includes('Insurance for key team members')
      )
        businessInsuranceTools.push('key_team_insurance');
      if (
        formValue.operationalTools?.includes(
          'Coverage for directors or officers',
        )
      )
        businessInsuranceTools.push('directors_coverage');

      // New Technology
      if (
        formValue.operationalTools?.includes(
          'Help using new digital tools in your business',
        )
      )
        newTechnologyTools.push('new_digital_tools');

      const toolsObj = {
        business_banking: businessBankingTools,
        marketing_help: marketingHelpTools,
        tech_support: techSupportTools,
        business_insurance: businessInsuranceTools,
        new_technology: newTechnologyTools,
      };

      (criteriaValues as any).business_operational_tools = {
        tools: toolsObj,
      };
    }

    // Only include Commercial Real Estate related fields if selected
    if (supportsLowercase.includes('commercial_real_estate')) {
      (criteriaValues as any).commercial_real_estate = {
        cre_help_locate_facilities: formValue.creHelpLocateFacilities === 'yes',
        cre_upgrade_facility: formValue.creUpgradeFacility === 'yes',
        cre_need_own_facility: formValue.creNeedOwnFacility === 'yes',
        cre_work_with_tenants: formValue.creWorkWithTenants === 'yes',
        cre_solar_energy_upgrades: formValue.creSolarEnergyUpgrades === 'yes',
      };
    }

    // Only include Solar and Energy related fields if selected
    if (supportsLowercase.includes('solar_and_energy')) {
      const solarLendingYes = formValue.solarLendingProducts === 'yes';
      const solarNeedCreditScoreYes =
        solarLendingYes && formValue.solarNeedCreditScore === 'yes';
      const batteryLendingYes = formValue.offersBatteryLending === 'yes';
      const batteryNeedCreditScoreYes =
        batteryLendingYes && formValue.batteryNeedCreditScore === 'yes';
      const evProductsYes = formValue.offersEvProducts === 'yes';
      const evNeedCreditScoreYes =
        evProductsYes && formValue.evNeedCreditScore === 'yes';

      (criteriaValues as any).solar_and_energy = {
        solar_lending_products: solarLendingYes,
        ...(solarLendingYes && {
          solar_loan_amount_range: {
            min: Number(formValue.solarLoanMinAmount) || 0,
            max: Number(formValue.solarLoanMaxAmount) || 0,
          },
          solar_need_credit_score: formValue.solarNeedCreditScore === 'yes',
          ...(solarNeedCreditScoreYes && {
            solar_credit_score_ranges: this.convertCreditScoreToApiSingle(
              formValue.solarCreditScoreRange || '',
            ),
          }),
          solar_loan_requirements: formValue.solarLoanRequirements || '',
        }),
        offers_energy_efficiency: formValue.offersEnergyEfficiency === 'yes',
        offers_battery_lending: batteryLendingYes,
        ...(batteryLendingYes && {
          battery_loan_amount_range: {
            min: Number(formValue.batteryLoanMinAmount) || 0,
            max: Number(formValue.batteryLoanMaxAmount) || 0,
          },
          battery_need_credit_score: formValue.batteryNeedCreditScore === 'yes',
          ...(batteryNeedCreditScoreYes && {
            battery_credit_score_ranges: this.convertCreditScoreToApiSingle(
              formValue.batteryCreditScoreRange || '',
            ),
          }),
          battery_loan_requirements: formValue.batteryLoanRequirements || '',
        }),
        need_power_use_statement: formValue.needPowerUseStatement === 'yes',
        offers_ev_products: evProductsYes,
        ...(evProductsYes && {
          ev_loan_amount_range: {
            min: Number(formValue.evLoanMinAmount) || 0,
            max: Number(formValue.evLoanMaxAmount) || 0,
          },
          ev_need_credit_score: formValue.evNeedCreditScore === 'yes',
          ...(evNeedCreditScoreYes && {
            ev_credit_score_ranges: this.convertCreditScoreToApiSingle(
              formValue.evCreditScoreRange || '',
            ),
          }),
          ev_loan_requirements: formValue.evLoanRequirements || '',
        }),
      };
    }

    // Only include Investing related fields if selected
    if (supportsLowercase.includes('finding_investors')) {
      const investmentTypesLowercase: string[] = [];
      if (formValue.investmentTypes?.includes('Equity Investing'))
        investmentTypesLowercase.push('equity');
      if (formValue.investmentTypes?.includes('Debt Investing'))
        investmentTypesLowercase.push('debt');
      if (formValue.investmentTypes?.includes('Revenue Sharing'))
        investmentTypesLowercase.push('revenue_sharing');

      (criteriaValues as any).finding_investors = {
        investment_types: investmentTypesLowercase,
        require_regional_investment:
          formValue.requireRegionalInvestment === 'yes',
      };
    }

    // Only include cities and zip codes if nationwide support is not enabled and there are valid entries
    if (formValue.nationwideSupport !== 'yes') {
      if (filteredCities.length > 0) {
        criteriaValues.cities = filteredCities;
      }

      if (filteredZipCodes.length > 0) {
        criteriaValues.zip_codes = filteredZipCodes;
      }
    }

    // Add mandatory match fields
    if (this.mandatoryMatchFields.size > 0) {
      criteriaValues.mandatory_match_fields = Array.from(
        this.mandatoryMatchFields,
      );
    }

    return {
      name: formValue.name,
      description: formValue.description || null,
      criteria: criteriaValues as LeadsCriteriaValues,
    };
  }

  loadLeadsCriteria(criteria: LeadsCriteria): void {
    // Use setTimeout to prevent UI blocking during heavy operations
    setTimeout(() => {
      this.loadLeadsCriteriaSync(criteria);
    }, 0);
  }

  private loadLeadsCriteriaSync(criteria: LeadsCriteria): void {
    // Clear existing arrays
    this.clearFormArrays();

    // Load name and description
    const loansCapital = (criteria.criteria as any).loans_and_capital || {};
    const techAssistance =
      (criteria.criteria as any).technical_assistance || {};
    const commRealEstate =
      (criteria.criteria as any).commercial_real_estate || {};
    const solarEnergy = (criteria.criteria as any).solar_and_energy || {};
    const findingInvestors = (criteria.criteria as any).finding_investors || {};

    this.matchingCriteriaForm.patchValue(
      {
        name: criteria.name,
        description: criteria.description || '',
        nationwideSupport: criteria.criteria.nationwide_support ? 'yes' : 'no',
        isMilitaryProgram:
          criteria.criteria.is_military_veteran_prog == null
            ? 'no'
            : criteria.criteria.is_military_veteran_prog
              ? 'yes'
              : 'no',
        isVeteranSpouse:
          criteria.criteria.is_veteran_spouse_prog == null
            ? 'no'
            : criteria.criteria.is_veteran_spouse_prog
              ? 'yes'
              : 'no',
        needBusinessEntity:
          criteria.criteria.need_business_entity == null
            ? ''
            : criteria.criteria.need_business_entity
              ? 'yes'
              : 'no',
        needStateRegistration:
          loansCapital.need_state_registration == null
            ? ''
            : loansCapital.need_state_registration
              ? 'yes'
              : 'no',
        hasTargetIndustries:
          techAssistance.has_target_industries == null
            ? ''
            : techAssistance.has_target_industries
              ? 'yes'
              : 'no',
        offersEmployeeOwnership:
          techAssistance.offers_employee_ownership == null
            ? ''
            : techAssistance.offers_employee_ownership
              ? 'yes'
              : 'no',
        taProgramsPaidService: techAssistance.ta_programs_paid_service || '',
        creHelpLocateFacilities:
          commRealEstate.cre_help_locate_facilities == null
            ? ''
            : commRealEstate.cre_help_locate_facilities
              ? 'yes'
              : 'no',
        creUpgradeFacility:
          commRealEstate.cre_upgrade_facility == null
            ? ''
            : commRealEstate.cre_upgrade_facility
              ? 'yes'
              : 'no',
        creNeedOwnFacility:
          commRealEstate.cre_need_own_facility == null
            ? ''
            : commRealEstate.cre_need_own_facility
              ? 'yes'
              : 'no',
        creWorkWithTenants:
          commRealEstate.cre_work_with_tenants == null
            ? ''
            : commRealEstate.cre_work_with_tenants
              ? 'yes'
              : 'no',
        creSolarEnergyUpgrades:
          commRealEstate.cre_solar_energy_upgrades == null
            ? ''
            : commRealEstate.cre_solar_energy_upgrades
              ? 'yes'
              : 'no',
        solarLendingProducts:
          solarEnergy.solar_lending_products == null
            ? ''
            : solarEnergy.solar_lending_products
              ? 'yes'
              : 'no',
        solarLoanMinAmount: solarEnergy.solar_loan_amount_range?.min || 0,
        solarLoanMaxAmount: solarEnergy.solar_loan_amount_range?.max || 0,
        solarNeedCreditScore:
          solarEnergy.solar_need_credit_score == null
            ? ''
            : solarEnergy.solar_need_credit_score
              ? 'yes'
              : 'no',
        solarLoanRequirements: solarEnergy.solar_loan_requirements || '',
        offersEnergyEfficiency:
          solarEnergy.offers_energy_efficiency == null
            ? ''
            : solarEnergy.offers_energy_efficiency
              ? 'yes'
              : 'no',
        offersBatteryLending:
          solarEnergy.offers_battery_lending == null
            ? ''
            : solarEnergy.offers_battery_lending
              ? 'yes'
              : 'no',
        batteryLoanMinAmount: solarEnergy.battery_loan_amount_range?.min || 0,
        batteryLoanMaxAmount: solarEnergy.battery_loan_amount_range?.max || 0,
        batteryNeedCreditScore:
          solarEnergy.battery_need_credit_score == null
            ? ''
            : solarEnergy.battery_need_credit_score
              ? 'yes'
              : 'no',
        batteryLoanRequirements: solarEnergy.battery_loan_requirements || '',
        needPowerUseStatement:
          solarEnergy.need_power_use_statement == null
            ? ''
            : solarEnergy.need_power_use_statement
              ? 'yes'
              : 'no',
        offersEvProducts:
          solarEnergy.offers_ev_products == null
            ? ''
            : solarEnergy.offers_ev_products
              ? 'yes'
              : 'no',
        evLoanMinAmount: solarEnergy.ev_loan_amount_range?.min || 0,
        evLoanMaxAmount: solarEnergy.ev_loan_amount_range?.max || 0,
        evNeedCreditScore:
          solarEnergy.ev_need_credit_score == null
            ? ''
            : solarEnergy.ev_need_credit_score
              ? 'yes'
              : 'no',
        evLoanRequirements: solarEnergy.ev_loan_requirements || '',
        requireRegionalInvestment:
          findingInvestors.require_regional_investment == null
            ? ''
            : findingInvestors.require_regional_investment
              ? 'yes'
              : 'no',
        cosignerHelps: loansCapital.cosigner_helps || '',
        needBusinessPlan: loansCapital.need_business_plan || '',
        needFinancialProjections: loansCapital.need_financial_projections || '',
        loanMinAmount: 0,
        loanMaxAmount: 0,
      },
      { emitEvent: false },
    );

    // Populate supported languages - convert lowercase to capitalized
    if (criteria.criteria.preferred_languages) {
      criteria.criteria.preferred_languages.forEach((lang: string) => {
        const capitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
        this.supportedLanguagesArray.push(this.fb.control(capitalizedLang), {
          emitEvent: false,
        });
      });
      this.supportedLanguagesArray.updateValueAndValidity({ emitEvent: false });
    }

    // States
    const selectedStates: string[] = criteria.criteria.states || [];
    selectedStates.forEach((state: string) => {
      this.statesOfServiceArray.push(this.fb.control(state), {
        emitEvent: false,
      });
    });
    this.statesOfServiceArray.updateValueAndValidity({ emitEvent: false });

    const savedCities: string[] = criteria.criteria.cities || [];
    const savedCounties: string[] = criteria.criteria.counties || [];
    const savedZipcodes: string[] = criteria.criteria.zip_codes || [];

    // restore counties list
    this.availableCounties = [...savedCounties];

    // restore cities list
    this.cities = savedCities.map(
      (name) =>
        ({
          name,
        }) as City,
    );

    // restore zipcodes list
    this.zipcodes = savedZipcodes.map(
      (code) =>
        ({
          code,
        }) as Zipcode,
    );

    // rebuild cityZipcodeMap (optional)
    savedCities.forEach((cityName) => {
      const city = this.cities.find((c) => c.name === cityName);
      if (city) {
        this.cityZipcodeMap.set(city.id, []);
      }
    });

    // Populate counties of service
    if (criteria.criteria.counties) {
      criteria.criteria.counties.forEach((county: string) => {
        this.countiesOfServiceArray.push(this.fb.control(county), {
          emitEvent: false,
        });
      });
      this.countiesOfServiceArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate cities
    if (criteria.criteria.cities) {
      criteria.criteria.cities.forEach((city: string) => {
        this.citiesArray.push(this.fb.control(city), { emitEvent: false });
      });
      this.citiesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate zip codes
    if (criteria.criteria.zip_codes) {
      criteria.criteria.zip_codes.forEach((zipCode) => {
        this.zipCodesArray.push(
          this.fb.control(zipCode, [this.zipCodeValidator]),
          { emitEvent: false },
        );
      });
      this.zipCodesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate business support - convert lowercase to display labels
    if (criteria.criteria.supports) {
      const supportMapping = [
        { key: 'loans_and_capital', label: 'Loans and Capital Products' },
        {
          key: 'technical_assistance',
          label: 'Small Business Technical Assistance',
        },
        {
          key: 'business_operational_tools',
          label: 'Business Operational Tools',
        },
        { key: 'commercial_real_estate', label: 'Commercial Real Estate' },
        {
          key: 'solar_and_energy',
          label: 'Solar and Energy Efficiency Products',
        },
        { key: 'finding_investors', label: 'Investing in Companies' },
        {
          key: 'chamber_or_networking',
          label: 'Chamber of Commerce Membership or Regional Networking Group',
        },
      ];

      criteria.criteria.supports.forEach((supportKey: string) => {
        const mapping = supportMapping.find((m) => m.key === supportKey);
        if (mapping) {
          this.businessSupportArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.businessSupportArray.updateValueAndValidity({ emitEvent: false });
    }
    // Populate target industries
    if (techAssistance.target_industries) {
      techAssistance.target_industries.forEach((industry: string) => {
        this.targetIndustriesArray.push(this.fb.control(industry), {
          emitEvent: false,
        });
      });
      this.targetIndustriesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate support areas - convert from lowercase array
    if (techAssistance.support_areas) {
      const supportAreaMapping = [
        { key: 'marketing', label: 'Marketing' },
        { key: 'finances_accounting', label: 'Finances/Accounting' },
        { key: 'strategy', label: 'Strategy' },
        { key: 'legal_assistance', label: 'Legal Assistance/Questions' },
        { key: 'business_succession', label: 'Business Succession Planning' },
        { key: 'applying_for_grant', label: 'Applying for a Grant' },
        { key: 'tax_structure', label: 'Tax Structure/Tax support' },
        { key: 'site_selection', label: 'Site Selection' },
        { key: 'human_resources', label: 'Human Resources' },
        { key: 'operational_planning', label: 'Operational Planning' },
        { key: 'applying_for_loan', label: 'Applying for a loan or capital' },
        { key: 'banking_accounts', label: 'Banking Accounts/Systems' },
        { key: 'technology', label: 'Technology' },
        { key: 'it_infrastructure', label: 'IT Infrastructure' },
        { key: 'investment_readiness', label: 'Investment Readiness Support' },
        { key: 'government_contracting', label: 'Government Contracting' },
        { key: 'manufacturing_support', label: 'Manufacturing Support' },
      ];

      techAssistance.support_areas.forEach((areaKey: string) => {
        const mapping = supportAreaMapping.find((m) => m.key === areaKey);
        if (mapping) {
          this.supportAreasArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.supportAreasArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate business help areas
    if (techAssistance.business_help_areas) {
      const businessHelpAreasMapping = [
        { key: 'business_plan', label: 'Creating a Business Plan' },
        { key: 'financial_forecast', label: 'Creating a Financial Forecast' },
        {
          key: 'business_structures',
          label: 'Understanding Business Structures',
        },
        {
          key: 'available_resources',
          label: 'Knowing what resources are available to support you',
        },
      ];

      techAssistance.business_help_areas.forEach((areaKey: string) => {
        const mapping = businessHelpAreasMapping.find((m) => m.key === areaKey);
        if (mapping) {
          this.businessHelpAreasArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.businessHelpAreasArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate paid support areas
    if (techAssistance.paid_support_areas) {
      const paidSupportAreasMapping = [
        { key: 'marketing', label: 'Marketing' },
        { key: 'finances_accounting', label: 'Finances/Accounting' },
        { key: 'strategy', label: 'Strategy' },
        { key: 'legal_assistance', label: 'Legal Assistance/Questions' },
        { key: 'business_succession', label: 'Business Succession Planning' },
        { key: 'applying_for_grant', label: 'Applying for a Grant' },
        { key: 'tax_structure', label: 'Tax Structure/Tax support' },
        { key: 'site_selection', label: 'Site Selection' },
        { key: 'human_resources', label: 'Human Resources' },
        { key: 'operational_planning', label: 'Operational Planning' },
        { key: 'applying_for_loan', label: 'Applying for a loan or capital' },
        { key: 'banking_accounts', label: 'Banking Accounts/Systems' },
        { key: 'technology', label: 'Technology' },
        { key: 'it_infrastructure', label: 'IT Infrastructure' },
        { key: 'investment_readiness', label: 'Investment Readiness Support' },
        { key: 'government_contracting', label: 'Government Contracting' },
        { key: 'manufacturing_support', label: 'Manufacturing Support' },
      ];

      techAssistance.paid_support_areas.forEach((areaKey: string) => {
        const mapping = paidSupportAreasMapping.find((m) => m.key === areaKey);
        if (mapping) {
          this.paidSupportAreasArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.paidSupportAreasArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate operational tools
    const businessTools =
      (criteria.criteria as any).business_operational_tools || {};
    if (businessTools.tools) {
      const operationalToolsObj = businessTools.tools;
      const operationalToolsMapping = [
        // Business Banking
        {
          category: 'business_banking',
          key: 'business_accounts',
          label:
            'Open or manage business accounts (checking, savings, investments)',
        },
        {
          category: 'business_banking',
          key: 'payroll_and_hr',
          label: 'Help with payroll and HR',
        },
        {
          category: 'business_banking',
          key: 'lines_of_credit',
          label: 'Get a line of credit',
        },
        {
          category: 'business_banking',
          key: 'manage_cash_flow',
          label: "Help managing your company's cash flow",
        },
        {
          category: 'business_banking',
          key: 'financial_strategy',
          label: 'Advice on financial strategy',
        },
        // Marketing Help
        {
          category: 'marketing_help',
          key: 'website_design',
          label:
            'Help designing your website or improving search engine visibility',
        },
        {
          category: 'marketing_help',
          key: 'physical_marketing',
          label: 'Help with flyers, signs, or other physical marketing',
        },
        {
          category: 'marketing_help',
          key: 'marketing_strategy',
          label: 'Help planning or improving your marketing strategy',
        },
        // Tech Support
        {
          category: 'tech_support',
          key: 'it_support',
          label: 'Help with IT setup, troubleshooting, and online security',
        },
        {
          category: 'tech_support',
          key: 'physical_infrastructure',
          label: 'Help setting up physical tech like servers, internet, etc.',
        },
        // Business Insurance
        {
          category: 'business_insurance',
          key: 'general_insurance',
          label: 'General business insurance',
        },
        {
          category: 'business_insurance',
          key: 'disaster_insurance',
          label: 'Insurance for disasters (flood, fire, etc.)',
        },
        {
          category: 'business_insurance',
          key: 'building_insurance',
          label: 'Property or building insurance',
        },
        {
          category: 'business_insurance',
          key: 'lawsuit_protection',
          label: 'Protection from lawsuits',
        },
        {
          category: 'business_insurance',
          key: 'key_team_insurance',
          label: 'Insurance for key team members',
        },
        {
          category: 'business_insurance',
          key: 'directors_coverage',
          label: 'Coverage for directors or officers',
        },
        // New Technology
        {
          category: 'new_technology',
          key: 'new_digital_tools',
          label: 'Help using new digital tools in your business',
        },
      ];

      operationalToolsMapping.forEach((mapping) => {
        const categoryArray =
          operationalToolsObj[
            mapping.category as keyof typeof operationalToolsObj
          ];
        if (categoryArray && categoryArray.includes(mapping.key)) {
          this.operationalToolsArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.operationalToolsArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate solar credit score range
    if (solarEnergy.solar_credit_score_ranges) {
      const displayRange = this.convertCreditScoreToDisplaySingle(
        solarEnergy.solar_credit_score_ranges,
      );
      this.matchingCriteriaForm.patchValue(
        { solarCreditScoreRange: displayRange },
        { emitEvent: false },
      );
    }

    // Populate battery credit score range
    if (solarEnergy.battery_credit_score_ranges) {
      const displayRange = this.convertCreditScoreToDisplaySingle(
        solarEnergy.battery_credit_score_ranges,
      );
      this.matchingCriteriaForm.patchValue(
        { batteryCreditScoreRange: displayRange },
        { emitEvent: false },
      );
    }

    // Populate EV credit score range
    if (solarEnergy.ev_credit_score_ranges) {
      const displayRange = this.convertCreditScoreToDisplaySingle(
        solarEnergy.ev_credit_score_ranges,
      );
      this.matchingCriteriaForm.patchValue(
        { evCreditScoreRange: displayRange },
        { emitEvent: false },
      );
    }

    // Populate investment types
    if (findingInvestors.investment_types) {
      const investmentTypesMapping = [
        { key: 'equity', label: 'Equity Investing' },
        { key: 'debt', label: 'Debt Investing' },
        { key: 'revenue_sharing', label: 'Revenue Sharing' },
      ];

      findingInvestors.investment_types.forEach((typeKey: string) => {
        const mapping = investmentTypesMapping.find((m) => m.key === typeKey);
        if (mapping) {
          this.investmentTypesArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.investmentTypesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate loan types
    if (loansCapital.loan_types && Array.isArray(loansCapital.loan_types)) {
      loansCapital.loan_types.forEach((loanTypeKey: string) => {
        const displayLabel = this.loanTypeReverseMapping[loanTypeKey];
        if (displayLabel) {
          this.loanTypesArray.push(this.fb.control(displayLabel), {
            emitEvent: false,
          });
        }
      });
      this.loanTypesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate loan amount range
    if (loansCapital.loan_size_range) {
      this.matchingCriteriaForm.patchValue({
        loanMinAmount: loansCapital.loan_size_range.min,
        loanMaxAmount: loansCapital.loan_size_range.max,
      });
    }

    // Populate financial documentation
    if (loansCapital.financial_documentations) {
      const financialDocsMapping = [
        { key: 'audited_statements', label: 'Audited financial statements' },
        {
          key: 'unaudited_statements',
          label: 'Unaudited formal financial statements',
        },
        { key: 'informal_statements', label: 'Informal financial statements' },
        { key: 'tax_returns', label: 'Tax returns' },
        { key: 'no_statements', label: 'No financial statements available' },
        { key: 'bank_statements', label: 'Bank Statements' },
      ];

      loansCapital.financial_documentations.forEach((docKey: string) => {
        const mapping = financialDocsMapping.find((m) => m.key === docKey);
        if (mapping) {
          this.financialDocArray.push(this.fb.control(mapping.label), {
            emitEvent: false,
          });
        }
      });
      this.financialDocArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate credit score range
    if (loansCapital.credit_score_range) {
      const displayRange = this.convertCreditScoreToDisplaySingle(
        loansCapital.credit_score_range,
      );
      this.matchingCriteriaForm.patchValue(
        { creditScoreRange: displayRange },
        { emitEvent: false },
      );
    }

    // Populate area types
    if (loansCapital.capital_deployment_areas) {
      const displayAreas = this.convertCapitalDeploymentAreasToDisplay(
        loansCapital.capital_deployment_areas,
      );
      displayAreas.forEach((areaType: string) => {
        this.areaTypesArray.push(this.fb.control(areaType), {
          emitEvent: false,
        });
      });
      this.areaTypesArray.updateValueAndValidity({ emitEvent: false });
    }

    // Populate mandatory match fields
    this.mandatoryMatchFields.clear();
    if (criteria.criteria.mandatory_match_fields) {
      criteria.criteria.mandatory_match_fields.forEach((field: string) => {
        this.mandatoryMatchFields.add(field);
      });
    }

    // Manually trigger nationwide support change to update validators
    const nationwideSupportValue = criteria.criteria.nationwide_support
      ? 'yes'
      : 'no';
    this.onNationwideSupportChange(nationwideSupportValue);

    // Re-apply conditional validators after patchValue(..., { emitEvent: false })
    this.updateConditionalValidations();
  }

  private preloadZipcodesForCities(cityIds: number[]): Observable<Zipcode[]> {
    return this.addressService.getZipcodesByCityIds(cityIds).pipe(
      takeUntil(this.destroy$),
      tap((zipcodes) => {
        cityIds.forEach((cityId) => {
          this.zipcodesCache.set(cityId, zipcodes);
        });
      }),
    );
  }

  private clearFormArrays(): void {
    // Use clear() method for O(1) performance instead of O(n²) while loops
    // Disable events to prevent unnecessary change detection during bulk operations
    this.supportedLanguagesArray.clear({ emitEvent: false });
    this.statesOfServiceArray.clear({ emitEvent: false });
    this.countiesOfServiceArray.clear({ emitEvent: false });
    this.citiesArray.clear({ emitEvent: false });
    this.zipCodesArray.clear({ emitEvent: false });
    this.businessSupportArray.clear({ emitEvent: false });
    this.loanTypesArray.clear({ emitEvent: false });
    this.financialDocArray.clear({ emitEvent: false });
    this.areaTypesArray.clear({ emitEvent: false });
    this.targetIndustriesArray.clear({ emitEvent: false });
    this.supportAreasArray.clear({ emitEvent: false });
    this.businessHelpAreasArray.clear({ emitEvent: false });
    this.paidSupportAreasArray.clear({ emitEvent: false });
    this.operationalToolsArray.clear({ emitEvent: false });
    this.investmentTypesArray.clear({ emitEvent: false });
  }

  private cleanupEmptyEntries(): void {
    // Remove empty cities
    for (let i = this.citiesArray.length - 1; i >= 0; i--) {
      const control = this.citiesArray.at(i);
      if (!control.value || control.value.trim() === '') {
        this.citiesArray.removeAt(i);
      }
    }

    // Remove empty zip codes
    for (let i = this.zipCodesArray.length - 1; i >= 0; i--) {
      const control = this.zipCodesArray.at(i);
      if (!control.value || control.value.trim() === '') {
        this.zipCodesArray.removeAt(i);
      }
    }
  }

  cleanupEmptyEntriesManually(): void {
    this.cleanupEmptyEntries();
  }

  private markFormGroupTouched(formGroup?: FormGroup): void {
    const group = formGroup || this.matchingCriteriaForm;
    Object.keys(group.controls).forEach((key) => {
      const control = group.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.markAsTouched();
        control.controls.forEach((c) => c.markAsTouched());
      } else {
        control?.markAsTouched();
      }
    });
  }

  // Custom validators
  private noBlankValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.trim() === '') {
      return { blank: true };
    }
    return null;
  }

  getErrorMessage(fieldName: string): string {
    const control = this.matchingCriteriaForm.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('invalidNumber')) {
      return 'Invalid number';
    }
    if (control.hasError('min')) {
      return `Value must be at least ${control.errors['min'].min}`;
    }
    if (control.hasError('max')) {
      return `Value must be less than or equal to ${control.errors['max'].max}`;
    }
    if (control.hasError('decimalPlaces')) {
      return `Value must not have more than ${control.errors['decimalPlaces'].max} decimal place(s)`;
    }
    if (control.hasError('maxDigits')) {
      const err = control.errors['maxDigits'];
      if (err.isWholeDigits) {
        return `Value must not exceed ${err.maxWholeDigits} whole number digit(s)`;
      }

      return `Value must not exceed ${err.max} total digit(s)`;
    }
    return '';
  }

  private loanAmountValidator = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const minAmount = formGroup.get('loanMinAmount')?.value;
    const maxAmount = formGroup.get('loanMaxAmount')?.value;

    const solarLoanMinAmount = formGroup.get('solarLoanMinAmount')?.value;
    const solarLoanMaxAmount = formGroup.get('solarLoanMaxAmount')?.value;

    const batteryLoanMinAmount = formGroup.get('batteryLoanMinAmount')?.value;
    const batteryLoanMaxAmount = formGroup.get('batteryLoanMaxAmount')?.value;

    const evLoanMinAmount = formGroup.get('evLoanMinAmount')?.value;
    const evLoanMaxAmount = formGroup.get('evLoanMaxAmount')?.value;

    // Only validate if loan amount range is required for this connection
    const requirements = getFieldRequirements(this.connection);
    if (!requirements.loanAmountRange) {
      return null;
    }

    if (minAmount && maxAmount && Number(minAmount) >= Number(maxAmount)) {
      return { invalidLoanRange: true };
    }

    if (
      solarLoanMinAmount &&
      solarLoanMaxAmount &&
      Number(solarLoanMinAmount) >= Number(solarLoanMaxAmount)
    ) {
      return { invalidSolarLoanRange: true };
    }

    if (
      batteryLoanMinAmount &&
      batteryLoanMaxAmount &&
      Number(batteryLoanMinAmount) >= Number(batteryLoanMaxAmount)
    ) {
      return { invalidBatteryLoanRange: true };
    }

    if (
      evLoanMinAmount &&
      evLoanMaxAmount &&
      Number(evLoanMinAmount) >= Number(evLoanMaxAmount)
    ) {
      return { invalidEVLoanRange: true };
    }
    return null;
  };

  private solarLoanAmountValidator = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const minAmount = formGroup.get('solarLoanMinAmount')?.value;
    const maxAmount = formGroup.get('solarLoanMaxAmount')?.value;
    if (minAmount && maxAmount && Number(minAmount) >= Number(maxAmount)) {
      return { invalidSolarLoanRange: true };
    }
    return null;
  };

  private batteryLoanAmountValidator = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const minAmount = formGroup.get('batteryLoanMinAmount')?.value;
    const maxAmount = formGroup.get('batteryLoanMaxAmount')?.value;
    if (minAmount && maxAmount && Number(minAmount) >= Number(maxAmount)) {
      return { invalidBatteryLoanRange: true };
    }
    return null;
  };

  private evLoanAmountValidator = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const minAmount = formGroup.get('evLoanMinAmount')?.value;
    const maxAmount = formGroup.get('evLoanMaxAmount')?.value;
    if (minAmount && maxAmount && Number(minAmount) >= Number(maxAmount)) {
      return { invalidEvLoanRange: true };
    }
    return null;
  };

  private atLeastOneSelectedValidator(
    array: AbstractControl,
  ): ValidationErrors | null {
    const formArray = array as FormArray;
    return formArray.length > 0 ? null : { atLeastOneSelected: true };
  }

  // Check if any empty input exists before allowing new additions
  private hasEmptyInput(array: FormArray): boolean {
    return array.controls.some(
      (control) => !control.value || control.value.trim() === '',
    );
  }

  // Zip code pattern validator
  private zipCodeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const zipPattern = /^\d{5}(-\d{4})?$/;
    return zipPattern.test(value) ? null : { invalidZipCode: true };
  }

  // Helper methods for template
  canAddCity(): boolean {
    return !this.hasEmptyInput(this.citiesArray);
  }

  canAddZipCode(): boolean {
    return !this.hasEmptyInput(this.zipCodesArray);
  }

  hasEmptyEntries(): boolean {
    return (
      this.hasEmptyInput(this.citiesArray) ||
      this.hasEmptyInput(this.zipCodesArray)
    );
  }

  getZipCodeError(index: number): string | null {
    const control = this.zipCodesArray.at(index);
    if (control.hasError('invalidZipCode') && control.touched) {
      return 'Please enter a valid zip code (e.g., 12345 or 12345-6789)';
    }
    return null;
  }

  getBusinessAgeLabel(age: string): string {
    switch (age) {
      case '<1 year':
        return '<1 year (start-ups)';
      case '1-2 years':
        return '1 - 2 Years';
      case '2-3 years':
        return '2 - 3 Years';
      case '4+ years':
        return '4+ Years';
      default:
        return age;
    }
  }

  // Convert credit score display labels to API values
  private convertCreditScoreToApi(displayValues: string[]): string[] {
    const mapping: Record<string, string> = {
      'No Credit score required': 'not_required',
      'Below 580': '<580',
      '580+': '580+',
      '670+': '670+',
      '740+': '740+',
      '800+': '800+',
    };
    return displayValues.map((val) => mapping[val] || val);
  }

  // Convert single credit score display label to API value
  private convertCreditScoreToApiSingle(displayValue: string): string {
    const mapping: Record<string, string> = {
      'No Credit score required': 'not_required',
      'Below 580': '<580',
      '580 and above': '580+',
      '670 and above': '670+',
      '740 and above': '740+',
      '800 and above': '800+',
    };
    return mapping[displayValue] || displayValue;
  }

  // Convert credit score API values to display labels
  private convertCreditScoreToDisplay(apiValues: string[]): string[] {
    const mapping: Record<string, string> = {
      not_required: 'No Credit score required',
      '<580': 'Below 580',
      '580+': '580 and above',
      '670+': '670 and above',
      '740+': '740 and above',
      '800+': '800 and above',
    };
    return apiValues.map((val) => mapping[val] || val);
  }

  // Convert single credit score API value to display label
  private convertCreditScoreToDisplaySingle(apiValue: string): string {
    const mapping: Record<string, string> = {
      not_required: 'No Credit score required',
      '<580': 'Below 580',
      '580+': '580 and above',
      '670+': '670 and above',
      '740+': '740 and above',
      '800+': '800 and above',
    };
    return mapping[apiValue] || apiValue;
  }

  // Convert capital deployment area display labels to API values (snake_case)
  private convertCapitalDeploymentAreasToApi(
    displayValues: string[],
  ): string[] {
    const mapping: Record<string, string> = {
      'Opportunity Zones': 'opportunity_zones',
      'New Market Tax Credit Area': 'new_market_tax_credit_area',
      'Low-to-moderate Income Areas': 'low_to_moderate_income_areas',
      'CDFI Investment Areas': 'cdfi_investment_areas',
      'LIDAC Defined Communities': 'lidac_defined_communities',
      Reservations: 'reservations',
      'USDA Rural defined areas': 'usda_rural_defined_areas',
    };
    return displayValues.map((val) => mapping[val] || val);
  }

  // Convert capital deployment area API values to display labels
  private convertCapitalDeploymentAreasToDisplay(
    apiValues: string[],
  ): string[] {
    const mapping: Record<string, string> = {
      opportunity_zones: 'Opportunity Zones',
      new_market_tax_credit_area: 'New Market Tax Credit Area',
      low_to_moderate_income_areas: 'Low-to-moderate Income Areas',
      cdfi_investment_areas: 'CDFI Investment Areas',
      lidac_defined_communities: 'LIDAC Defined Communities',
      reservations: 'Reservations',
      usda_rural_defined_areas: 'USDA Rural defined areas',
    };
    return apiValues.map((val) => mapping[val] || val);
  }

  // Mode switching methods
  onEdit(): void {
    // Prevent editing if criteria is deprecated
    if (this.savedCriteria?.deprecated) {
      return;
    }

    this.isEditMode = true;
    this.updateConditionalValidations();

    const selectedStates = Array.from(
      new Set((this.statesOfServiceArray.value as string[]) || []),
    );
    const selectedCities = Array.from(
      new Set((this.citiesArray.value as string[]) || []),
    );

    this.getCachedOrFetchStates()
      .pipe(
        tap((states) => {
          this.states = states;
        }),

        // bulk load counties + cities for the selected states
        switchMap(() => {
          const selectedStateIds = Array.from(
            new Set(
              selectedStates
                .map((stateName: string) =>
                  this.states.find((s) => s.name === stateName),
                )
                .filter((s): s is State => !!s)
                .map((s) => s.id),
            ),
          );

          if (!selectedStateIds.length) {
            return of({ counties: [], cities: [] } as {
              counties: County[];
              cities: City[];
            });
          }

          return this.getCachedOrFetchCitiesAndCounties(selectedStateIds);
        }),

        tap(({ counties, cities }) => {
          this.counties = counties;
          this.availableCounties = [...new Set(counties.map((c) => c.name))];

          this.cities = cities.filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          );
        }),

        // bulk load zipcodes for selected cities
        switchMap(() => {
          const selectedCityIds = Array.from(
            new Set(
              selectedCities
                .map((cityName: string) =>
                  this.cities.find((c) => c.name === cityName),
                )
                .filter((c): c is City => !!c)
                .map((c) => c.id),
            ),
          );

          if (!selectedCityIds.length) {
            return of([] as Zipcode[]);
          }

          return this.preloadZipcodesForCities(selectedCityIds);
        }),

        tap((zipcodes) => {
          this.zipcodes = zipcodes.filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          );
        }),

        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onCancel(): void {
    if (this.savedCriteria) {
      this.loadLeadsCriteria(this.savedCriteria);
      this.isEditMode = false;
    }
  }

  // Check if Loans and Capital Products is selected
  isLoansAndCapitalSelected(): boolean {
    const businessSupportArray = this.businessSupportArray;
    return businessSupportArray.value.includes('Loans and Capital Products');
  }

  // Check if Small Business Technical Assistance is selected
  isTechnicalAssistanceSelected(): boolean {
    const businessSupportArray = this.businessSupportArray;
    return businessSupportArray.value.includes(
      'Small Business Technical Assistance',
    );
  }

  // Check if Business Operational Tools is selected
  isBusinessOperationalToolsSelected(): boolean {
    const businessSupportArray = this.businessSupportArray;
    return businessSupportArray.value.includes('Business Operational Tools');
  }

  // Check if Looking at Commercial Real Estate is selected
  isCommercialRealEstateSelected(): boolean {
    const businessSupportArray = this.businessSupportArray;
    return businessSupportArray.value.includes('Commercial Real Estate');
  }

  // Check if Solar and Energy Efficiency Products is selected
  isSolarEnergySelected(): boolean {
    const businessSupportArray = this.businessSupportArray;
    return businessSupportArray.value.includes(
      'Solar and Energy Efficiency Products',
    );
  }

  // Check if solar lending products is yes
  isSolarLendingYes(): boolean {
    return (
      this.matchingCriteriaForm.get('solarLendingProducts')?.value === 'yes'
    );
  }

  // Check if solar need credit score is yes
  isSolarNeedCreditScoreYes(): boolean {
    return (
      this.matchingCriteriaForm.get('solarNeedCreditScore')?.value === 'yes'
    );
  }

  // Check if battery lending is yes
  isBatteryLendingYes(): boolean {
    return (
      this.matchingCriteriaForm.get('offersBatteryLending')?.value === 'yes'
    );
  }

  // Check if battery need credit score is yes
  isBatteryNeedCreditScoreYes(): boolean {
    return (
      this.matchingCriteriaForm.get('batteryNeedCreditScore')?.value === 'yes'
    );
  }

  // Check if EV products is yes
  isEvProductsYes(): boolean {
    return this.matchingCriteriaForm.get('offersEvProducts')?.value === 'yes';
  }

  // Check if EV need credit score is yes
  isEvNeedCreditScoreYes(): boolean {
    return this.matchingCriteriaForm.get('evNeedCreditScore')?.value === 'yes';
  }

  // Check if Investing in Companies is selected
  isInvestingInCompaniesSelected(): boolean {
    return this.businessSupportArray.value.includes('Investing in Companies');
  }

  // Check if industry is selected
  isIndustrySelected(industryValue: string): boolean {
    return this.targetIndustriesArray.value.includes(industryValue);
  }

  // Toggle industry selection
  onIndustryToggle(industryValue: string): void {
    const industriesArray = this.targetIndustriesArray;
    const index = industriesArray.value.indexOf(industryValue);

    if (index > -1) {
      industriesArray.removeAt(index);
    } else {
      industriesArray.push(this.fb.control(industryValue));
    }

    industriesArray.markAsTouched();
    industriesArray.markAsDirty();
  }

  // Check if Business Succession Planning is selected
  isBusinessSuccessionSelected(): boolean {
    return this.supportAreasArray.value.includes(
      'Business Succession Planning',
    );
  }

  // Check if TA programs paid service is 'some'
  isTaProgramsSomeOfThem(): boolean {
    return (
      this.matchingCriteriaForm.get('taProgramsPaidService')?.value === 'some'
    );
  }

  // Handler for CRE solar energy upgrades change
  onNationwideSupportChange(value: string): void {
    if (value === 'yes') {
      // If nationwide support is yes, clear location fields and remove validation
      this.statesOfServiceArray.clear();
      this.countiesOfServiceArray.clear();
      this.citiesArray.clear();
      this.zipCodesArray.clear();

      this.statesOfServiceArray.clearValidators();
      this.countiesOfServiceArray.clearValidators();

      this.statesOfServiceArray.updateValueAndValidity();
      this.countiesOfServiceArray.updateValueAndValidity();

      // Clear location data
      this.availableCounties = [];
      this.counties = [];
      this.cities = [];
      this.zipcodes = [];
    } else if (value === 'no') {
      // If nationwide support is no, add validation back
      this.statesOfServiceArray.setValidators([
        Validators.required,
        this.atLeastOneSelectedValidator,
      ]);
      this.countiesOfServiceArray.setValidators([
        Validators.required,
        this.atLeastOneSelectedValidator,
      ]);

      this.statesOfServiceArray.updateValueAndValidity();
      this.countiesOfServiceArray.updateValueAndValidity();
    }
  }

  onCreSolarEnergyUpgradesChange(value: string): void {
    const solarOption = 'Solar and Energy Efficiency Products';
    const businessSupportValues = this.businessSupportArray.value;
    const index = businessSupportValues.indexOf(solarOption);

    if (value === 'yes') {
      // Add the solar option if not already selected
      if (index === -1) {
        this.businessSupportArray.push(this.fb.control(solarOption));
        this.businessSupportArray.markAsTouched();
      }
    } else if (value === 'no') {
      // Remove the solar option if it exists
      if (index > -1) {
        this.businessSupportArray.removeAt(index);
        this.businessSupportArray.markAsTouched();
      }
    }
  }

  // Update conditional validations based on selected business support options
  updateConditionalValidations(): void {
    if (this.isUpdatingConditionalValidations) {
      return;
    }

    this.isUpdatingConditionalValidations = true;

    try {
      const businessSupportValues = this.businessSupportArray.value || [];
      const isLoansSelected = businessSupportValues.includes(
        'Loans and Capital Products',
      );
      const isTechnicalAssistanceSelected = businessSupportValues.includes(
        'Small Business Technical Assistance',
      );
      const isOperationalToolsSelected = businessSupportValues.includes(
        'Business Operational Tools',
      );
      const isCRESelected = businessSupportValues.includes(
        'Commercial Real Estate',
      );
      const isSolarSelected = businessSupportValues.includes(
        'Solar and Energy Efficiency Products',
      );
      const isInvestingSelected = businessSupportValues.includes(
        'Investing in Companies',
      );

      // Loans and Capital Products validators
      if (isLoansSelected) {
        this.matchingCriteriaForm
          .get('needStateRegistration')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('cosignerHelps')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('needBusinessPlan')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('needFinancialProjections')
          ?.setValidators([Validators.required]);
        this.loanTypesArray.setValidators(
          this.connection === 'dh::nc-sbtdc-ssbci'
            ? []
            : [Validators.required, this.atLeastOneSelectedValidator],
        );
        this.matchingCriteriaForm
          .get('loanMinAmount')
          ?.setValidators([
            Validators.required,
            this.numberValidatorService.numberValidator('loanMinAmount'),
          ]);
        this.matchingCriteriaForm
          .get('loanMaxAmount')
          ?.setValidators([
            Validators.required,
            this.numberValidatorService.numberValidator('loanMaxAmount'),
          ]);
        this.financialDocArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);
        this.matchingCriteriaForm
          .get('creditScoreRange')
          ?.setValidators([Validators.required]);
        this.areaTypesArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);
      } else {
        this.matchingCriteriaForm
          .get('needStateRegistration')
          ?.clearValidators();
        this.matchingCriteriaForm.get('cosignerHelps')?.clearValidators();
        this.matchingCriteriaForm.get('needBusinessPlan')?.clearValidators();
        this.matchingCriteriaForm
          .get('needFinancialProjections')
          ?.clearValidators();
        this.loanTypesArray.clearValidators();
        this.matchingCriteriaForm.get('loanMinAmount')?.clearValidators();
        this.matchingCriteriaForm.get('loanMaxAmount')?.clearValidators();
        this.financialDocArray.clearValidators();
        this.matchingCriteriaForm.get('creditScoreRange')?.clearValidators();
        this.areaTypesArray.clearValidators();
      }

      // Technical Assistance validators
      if (isTechnicalAssistanceSelected) {
        this.matchingCriteriaForm
          .get('hasTargetIndustries')
          ?.setValidators([Validators.required]);

        if (
          this.matchingCriteriaForm.get('hasTargetIndustries')?.value === 'yes'
        ) {
          this.targetIndustriesArray.setValidators([
            Validators.required,
            this.atLeastOneSelectedValidator,
          ]);
        } else {
          this.targetIndustriesArray.clearValidators();
        }

        this.supportAreasArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);

        if (this.isBusinessSuccessionSelected()) {
          this.matchingCriteriaForm
            .get('offersEmployeeOwnership')
            ?.setValidators([Validators.required]);
        } else {
          this.matchingCriteriaForm
            .get('offersEmployeeOwnership')
            ?.clearValidators();
        }

        this.matchingCriteriaForm
          .get('taProgramsPaidService')
          ?.setValidators([Validators.required]);

        if (this.isTaProgramsSomeOfThem()) {
          this.paidSupportAreasArray.setValidators([
            Validators.required,
            this.atLeastOneSelectedValidator,
          ]);
        } else {
          this.paidSupportAreasArray.clearValidators();
        }

        this.businessHelpAreasArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);
      } else {
        this.matchingCriteriaForm.get('hasTargetIndustries')?.clearValidators();
        this.targetIndustriesArray.clearValidators();
        this.supportAreasArray.clearValidators();
        this.matchingCriteriaForm
          .get('offersEmployeeOwnership')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('taProgramsPaidService')
          ?.clearValidators();
        this.paidSupportAreasArray.clearValidators();
        this.businessHelpAreasArray.clearValidators();
      }

      // Business Operational Tools validators
      if (isOperationalToolsSelected) {
        this.operationalToolsArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);
      } else {
        this.operationalToolsArray.clearValidators();
      }

      // Commercial Real Estate validators
      if (isCRESelected) {
        this.matchingCriteriaForm
          .get('creHelpLocateFacilities')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('creUpgradeFacility')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('creNeedOwnFacility')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('creWorkWithTenants')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('creSolarEnergyUpgrades')
          ?.setValidators([Validators.required]);
      } else {
        this.matchingCriteriaForm
          .get('creHelpLocateFacilities')
          ?.clearValidators();
        this.matchingCriteriaForm.get('creUpgradeFacility')?.clearValidators();
        this.matchingCriteriaForm.get('creNeedOwnFacility')?.clearValidators();
        this.matchingCriteriaForm.get('creWorkWithTenants')?.clearValidators();
        this.matchingCriteriaForm
          .get('creSolarEnergyUpgrades')
          ?.clearValidators();
      }

      // Solar and Energy Efficiency Products validators
      if (isSolarSelected) {
        this.matchingCriteriaForm
          .get('solarLendingProducts')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('offersEnergyEfficiency')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('offersBatteryLending')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('needPowerUseStatement')
          ?.setValidators([Validators.required]);
        this.matchingCriteriaForm
          .get('offersEvProducts')
          ?.setValidators([Validators.required]);

        const solarLendingValue = this.matchingCriteriaForm.get(
          'solarLendingProducts',
        )?.value;
        const solarNeedCreditScoreValue = this.matchingCriteriaForm.get(
          'solarNeedCreditScore',
        )?.value;
        const batteryLendingValue = this.matchingCriteriaForm.get(
          'offersBatteryLending',
        )?.value;
        const batteryNeedCreditScoreValue = this.matchingCriteriaForm.get(
          'batteryNeedCreditScore',
        )?.value;
        const evProductsValue =
          this.matchingCriteriaForm.get('offersEvProducts')?.value;
        const evNeedCreditScoreValue =
          this.matchingCriteriaForm.get('evNeedCreditScore')?.value;

        if (solarLendingValue === 'yes') {
          this.matchingCriteriaForm
            .get('solarLoanMinAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator('solarLoanMinAmount'),
            ]);
          this.matchingCriteriaForm
            .get('solarLoanMaxAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator('solarLoanMaxAmount'),
            ]);
          this.matchingCriteriaForm
            .get('solarNeedCreditScore')
            ?.setValidators([Validators.required]);
          this.matchingCriteriaForm
            .get('solarLoanRequirements')
            ?.setValidators([Validators.required, this.noBlankValidator]);

          if (solarNeedCreditScoreValue === 'yes') {
            this.matchingCriteriaForm
              .get('solarCreditScoreRange')
              ?.setValidators([Validators.required]);
          } else {
            this.matchingCriteriaForm
              .get('solarCreditScoreRange')
              ?.clearValidators();
          }
        } else {
          this.matchingCriteriaForm
            .get('solarLoanMinAmount')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('solarLoanMaxAmount')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('solarNeedCreditScore')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('solarCreditScoreRange')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('solarLoanRequirements')
            ?.clearValidators();
        }

        if (batteryLendingValue === 'yes') {
          this.matchingCriteriaForm
            .get('batteryLoanMinAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator(
                'batteryLoanMinAmount',
              ),
            ]);
          this.matchingCriteriaForm
            .get('batteryLoanMaxAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator(
                'batteryLoanMaxAmount',
              ),
            ]);
          this.matchingCriteriaForm
            .get('batteryNeedCreditScore')
            ?.setValidators([Validators.required]);
          this.matchingCriteriaForm
            .get('batteryLoanRequirements')
            ?.setValidators([Validators.required, this.noBlankValidator]);

          if (batteryNeedCreditScoreValue === 'yes') {
            this.matchingCriteriaForm
              .get('batteryCreditScoreRange')
              ?.setValidators([Validators.required]);
          } else {
            this.matchingCriteriaForm
              .get('batteryCreditScoreRange')
              ?.clearValidators();
          }
        } else {
          this.matchingCriteriaForm
            .get('batteryLoanMinAmount')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('batteryLoanMaxAmount')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('batteryNeedCreditScore')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('batteryCreditScoreRange')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('batteryLoanRequirements')
            ?.clearValidators();
        }

        if (evProductsValue === 'yes') {
          this.matchingCriteriaForm
            .get('evLoanMinAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator('evLoanMinAmount'),
            ]);
          this.matchingCriteriaForm
            .get('evLoanMaxAmount')
            ?.setValidators([
              Validators.required,
              this.numberValidatorService.numberValidator('evLoanMaxAmount'),
            ]);
          this.matchingCriteriaForm
            .get('evNeedCreditScore')
            ?.setValidators([Validators.required]);
          this.matchingCriteriaForm
            .get('evLoanRequirements')
            ?.setValidators([Validators.required, this.noBlankValidator]);

          if (evNeedCreditScoreValue === 'yes') {
            this.matchingCriteriaForm
              .get('evCreditScoreRange')
              ?.setValidators([Validators.required]);
          } else {
            this.matchingCriteriaForm
              .get('evCreditScoreRange')
              ?.clearValidators();
          }
        } else {
          this.matchingCriteriaForm.get('evLoanMinAmount')?.clearValidators();
          this.matchingCriteriaForm.get('evLoanMaxAmount')?.clearValidators();
          this.matchingCriteriaForm.get('evNeedCreditScore')?.clearValidators();
          this.matchingCriteriaForm
            .get('evCreditScoreRange')
            ?.clearValidators();
          this.matchingCriteriaForm
            .get('evLoanRequirements')
            ?.clearValidators();
        }
      } else {
        this.matchingCriteriaForm
          .get('solarLendingProducts')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('offersEnergyEfficiency')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('offersBatteryLending')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('needPowerUseStatement')
          ?.clearValidators();
        this.matchingCriteriaForm.get('offersEvProducts')?.clearValidators();

        this.matchingCriteriaForm.get('solarLoanMinAmount')?.clearValidators();
        this.matchingCriteriaForm.get('solarLoanMaxAmount')?.clearValidators();
        this.matchingCriteriaForm
          .get('solarNeedCreditScore')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('solarCreditScoreRange')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('solarLoanRequirements')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('batteryLoanMinAmount')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('batteryLoanMaxAmount')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('batteryNeedCreditScore')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('batteryCreditScoreRange')
          ?.clearValidators();
        this.matchingCriteriaForm
          .get('batteryLoanRequirements')
          ?.clearValidators();
        this.matchingCriteriaForm.get('evLoanMinAmount')?.clearValidators();
        this.matchingCriteriaForm.get('evLoanMaxAmount')?.clearValidators();
        this.matchingCriteriaForm.get('evNeedCreditScore')?.clearValidators();
        this.matchingCriteriaForm.get('evCreditScoreRange')?.clearValidators();
        this.matchingCriteriaForm.get('evLoanRequirements')?.clearValidators();
      }

      // Investing in Companies validators
      if (isInvestingSelected) {
        this.investmentTypesArray.setValidators([
          Validators.required,
          this.atLeastOneSelectedValidator,
        ]);
        this.matchingCriteriaForm
          .get('requireRegionalInvestment')
          ?.setValidators([Validators.required]);
      } else {
        this.investmentTypesArray.clearValidators();
        this.matchingCriteriaForm
          .get('requireRegionalInvestment')
          ?.clearValidators();
      }

      // Update validity for all fields
      this.matchingCriteriaForm
        .get('needStateRegistration')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm.get('cosignerHelps')?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('needBusinessPlan')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('needFinancialProjections')
        ?.updateValueAndValidity();
      this.loanTypesArray.updateValueAndValidity();
      this.matchingCriteriaForm.get('loanMinAmount')?.updateValueAndValidity();
      this.matchingCriteriaForm.get('loanMaxAmount')?.updateValueAndValidity();
      this.financialDocArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creditScoreRange')
        ?.updateValueAndValidity();
      this.areaTypesArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('hasTargetIndustries')
        ?.updateValueAndValidity();
      this.targetIndustriesArray.updateValueAndValidity();
      this.supportAreasArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('offersEmployeeOwnership')
        ?.updateValueAndValidity();
      this.businessHelpAreasArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('taProgramsPaidService')
        ?.updateValueAndValidity();
      this.paidSupportAreasArray.updateValueAndValidity();
      this.operationalToolsArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creHelpLocateFacilities')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creUpgradeFacility')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creNeedOwnFacility')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creWorkWithTenants')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('creSolarEnergyUpgrades')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarLendingProducts')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarLoanMinAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarLoanMaxAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarNeedCreditScore')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarCreditScoreRange')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('solarLoanRequirements')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('offersEnergyEfficiency')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('offersBatteryLending')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('batteryLoanMinAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('batteryLoanMaxAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('batteryNeedCreditScore')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('batteryCreditScoreRange')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('batteryLoanRequirements')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('needPowerUseStatement')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('offersEvProducts')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('evLoanMinAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('evLoanMaxAmount')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('evNeedCreditScore')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('evCreditScoreRange')
        ?.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('evLoanRequirements')
        ?.updateValueAndValidity();
      this.investmentTypesArray.updateValueAndValidity();
      this.matchingCriteriaForm
        .get('requireRegionalInvestment')
        ?.updateValueAndValidity();
    } finally {
      this.isUpdatingConditionalValidations = false;
    }
  }

  // Get selected support areas for paid services selection
  get selectedSupportAreas(): string[] {
    return this.supportAreasArray.value || [];
  }

  // Convert supports array to display labels
  getSupportsArray(): string[] {
    if (!this.savedCriteria?.criteria?.supports) return [];

    return this.savedCriteria.criteria.supports
      .map((key: string) => BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME[key])
      .filter((label: string | undefined) => label !== undefined);
  }

  // Convert preferred languages array to capitalized strings for display
  getPreferredLanguagesArray(): string[] {
    if (!this.savedCriteria?.criteria?.preferred_languages) return [];

    return this.savedCriteria.criteria.preferred_languages.map(
      (lang: string) => lang.charAt(0).toUpperCase() + lang.slice(1),
    );
  }

  // Convert support areas array to display labels
  getSupportAreasArray(): string[] {
    if (!this.savedCriteria?.criteria?.technical_assistance?.support_areas)
      return [];

    const supportAreaMapping = [
      { key: 'marketing', label: 'Marketing' },
      { key: 'finances_accounting', label: 'Finances/Accounting' },
      { key: 'strategy', label: 'Strategy' },
      { key: 'legal_assistance', label: 'Legal Assistance/Questions' },
      { key: 'business_succession', label: 'Business Succession Planning' },
      { key: 'applying_for_grant', label: 'Applying for a Grant' },
      { key: 'tax_structure', label: 'Tax Structure/Tax support' },
      { key: 'site_selection', label: 'Site Selection' },
      { key: 'human_resources', label: 'Human Resources' },
      { key: 'operational_planning', label: 'Operational Planning' },
      { key: 'applying_for_loan', label: 'Applying for a loan or capital' },
      { key: 'banking_accounts', label: 'Banking Accounts/Systems' },
      { key: 'technology', label: 'Technology' },
      { key: 'it_infrastructure', label: 'IT Infrastructure' },
      { key: 'investment_readiness', label: 'Investment Readiness Support' },
      { key: 'government_contracting', label: 'Government Contracting' },
      { key: 'manufacturing_support', label: 'Manufacturing Support' },
    ];

    const areas: string[] = [];
    this.savedCriteria.criteria.technical_assistance.support_areas.forEach(
      (areaKey: string) => {
        const mapping = supportAreaMapping.find((m) => m.key === areaKey);
        if (mapping) {
          areas.push(mapping.label);
        }
      },
    );

    return areas;
  }

  getCapitalDeploymentAreasArray(): string[] {
    if (
      !this.savedCriteria?.criteria?.loans_and_capital?.capital_deployment_areas
    )
      return [];

    return this.convertCapitalDeploymentAreasToDisplay(
      this.savedCriteria.criteria.loans_and_capital.capital_deployment_areas,
    );
  }

  getCreditScoreRange(): string {
    if (!this.savedCriteria?.criteria?.loans_and_capital?.credit_score_range)
      return '';

    return this.convertCreditScoreToDisplaySingle(
      this.savedCriteria.criteria.loans_and_capital.credit_score_range,
    );
  }

  getSolarCreditScoreRange(): string {
    if (
      !this.savedCriteria?.criteria?.solar_and_energy?.solar_credit_score_ranges
    )
      return '';

    return this.convertCreditScoreToDisplaySingle(
      this.savedCriteria.criteria.solar_and_energy.solar_credit_score_ranges,
    );
  }

  getBatteryCreditScoreRange(): string {
    if (
      !this.savedCriteria?.criteria?.solar_and_energy
        ?.battery_credit_score_ranges
    )
      return '';

    return this.convertCreditScoreToDisplaySingle(
      this.savedCriteria.criteria.solar_and_energy.battery_credit_score_ranges,
    );
  }

  getEvCreditScoreRange(): string {
    if (!this.savedCriteria?.criteria?.solar_and_energy?.ev_credit_score_ranges)
      return '';

    return this.convertCreditScoreToDisplaySingle(
      this.savedCriteria.criteria.solar_and_energy.ev_credit_score_ranges,
    );
  }

  getPaidSupportAreasArray(): string[] {
    if (!this.savedCriteria?.criteria?.technical_assistance?.paid_support_areas)
      return [];

    const paidSupportAreasMapping = [
      { key: 'marketing', label: 'Marketing' },
      { key: 'finances_accounting', label: 'Finances/Accounting' },
      { key: 'strategy', label: 'Strategy' },
      { key: 'legal_assistance', label: 'Legal Assistance/Questions' },
      { key: 'business_succession', label: 'Business Succession Planning' },
      { key: 'applying_for_grant', label: 'Applying for a Grant' },
      { key: 'tax_structure', label: 'Tax Structure/Tax support' },
      { key: 'site_selection', label: 'Site Selection' },
      { key: 'human_resources', label: 'Human Resources' },
      { key: 'operational_planning', label: 'Operational Planning' },
      { key: 'applying_for_loan', label: 'Applying for a loan or capital' },
      { key: 'banking_accounts', label: 'Banking Accounts/Systems' },
      { key: 'technology', label: 'Technology' },
      { key: 'it_infrastructure', label: 'IT Infrastructure' },
      { key: 'investment_readiness', label: 'Investment Readiness Support' },
      { key: 'government_contracting', label: 'Government Contracting' },
      { key: 'manufacturing_support', label: 'Manufacturing Support' },
    ];

    const areas: string[] = [];
    this.savedCriteria.criteria.technical_assistance.paid_support_areas.forEach(
      (areaKey: string) => {
        const mapping = paidSupportAreasMapping.find((m) => m.key === areaKey);
        if (mapping) {
          areas.push(mapping.label);
        }
      },
    );

    return areas;
  }

  getBusinessHelpAreasArray(): string[] {
    if (
      !this.savedCriteria?.criteria?.technical_assistance?.business_help_areas
    )
      return [];

    const businessHelpAreasMapping = [
      { key: 'business_plan', label: 'Creating a Business Plan' },
      { key: 'financial_forecast', label: 'Creating a Financial Forecast' },
      {
        key: 'business_structures',
        label: 'Understanding Business Structures',
      },
      {
        key: 'available_resources',
        label: 'Knowing what resources are available to support you',
      },
    ];

    const areas: string[] = [];
    this.savedCriteria.criteria.technical_assistance.business_help_areas.forEach(
      (areaKey: string) => {
        const mapping = businessHelpAreasMapping.find((m) => m.key === areaKey);
        if (mapping) {
          areas.push(mapping.label);
        }
      },
    );

    return areas;
  }

  getOperationalToolsCategorized(): { category: string; tools: string[] }[] {
    if (!this.savedCriteria?.criteria?.business_operational_tools?.tools)
      return [];

    const operationalToolsObj =
      this.savedCriteria.criteria.business_operational_tools.tools;
    const operationalToolsMapping = [
      // Business Banking
      {
        category: 'business_banking',
        key: 'business_accounts',
        label:
          'Open or manage business accounts (checking, savings, investments)',
      },
      {
        category: 'business_banking',
        key: 'payroll_and_hr',
        label: 'Help with payroll and HR',
      },
      {
        category: 'business_banking',
        key: 'lines_of_credit',
        label: 'Get a line of credit',
      },
      {
        category: 'business_banking',
        key: 'manage_cash_flow',
        label: "Help managing your company's cash flow",
      },
      {
        category: 'business_banking',
        key: 'financial_strategy',
        label: 'Advice on financial strategy',
      },
      // Marketing Help
      {
        category: 'marketing_help',
        key: 'website_design',
        label:
          'Help designing your website or improving search engine visibility',
      },
      {
        category: 'marketing_help',
        key: 'physical_marketing',
        label: 'Help with flyers, signs, or other physical marketing',
      },
      {
        category: 'marketing_help',
        key: 'marketing_strategy',
        label: 'Help planning or improving your marketing strategy',
      },
      // Tech Support
      {
        category: 'tech_support',
        key: 'it_support',
        label: 'Help with IT setup, troubleshooting, and online security',
      },
      {
        category: 'tech_support',
        key: 'physical_infrastructure',
        label: 'Help setting up physical tech like servers, internet, etc.',
      },
      // Business Insurance
      {
        category: 'business_insurance',
        key: 'general_insurance',
        label: 'General business insurance',
      },
      {
        category: 'business_insurance',
        key: 'disaster_insurance',
        label: 'Insurance for disasters (flood, fire, etc.)',
      },
      {
        category: 'business_insurance',
        key: 'building_insurance',
        label: 'Property or building insurance',
      },
      {
        category: 'business_insurance',
        key: 'lawsuit_protection',
        label: 'Protection from lawsuits',
      },
      {
        category: 'business_insurance',
        key: 'key_team_insurance',
        label: 'Insurance for key team members',
      },
      {
        category: 'business_insurance',
        key: 'directors_coverage',
        label: 'Coverage for directors or officers',
      },
      // New Technology
      {
        category: 'new_technology',
        key: 'new_digital_tools',
        label: 'Help using new digital tools in your business',
      },
    ];

    const categoryMap: Record<string, { category: string; tools: string[] }> = {
      business_banking: { category: 'Business Banking', tools: [] },
      marketing_help: { category: 'Marketing Help', tools: [] },
      tech_support: { category: 'Tech Support', tools: [] },
      business_insurance: { category: 'Business Insurance', tools: [] },
      new_technology: { category: 'New Technology', tools: [] },
    };

    operationalToolsMapping.forEach((mapping) => {
      const categoryArray =
        operationalToolsObj[
          mapping.category as keyof typeof operationalToolsObj
        ];
      if (categoryArray && categoryArray.includes(mapping.key)) {
        categoryMap[mapping.category].tools.push(mapping.label);
      }
    });

    return Object.values(categoryMap).filter((cat) => cat.tools.length > 0);
  }

  getOperationalToolsArray(): string[] {
    const categorized = this.getOperationalToolsCategorized();
    const allTools: string[] = [];
    categorized.forEach((cat) => {
      allTools.push(...cat.tools);
    });
    return allTools;
  }

  getInvestmentTypesArray(): string[] {
    if (!this.savedCriteria?.criteria?.finding_investors?.investment_types)
      return [];

    const investmentTypesMapping = [
      { key: 'equity', label: 'Equity Investing' },
      { key: 'debt', label: 'Debt Investing' },
      { key: 'revenue_sharing', label: 'Revenue Sharing' },
    ];

    const types: string[] = [];
    this.savedCriteria.criteria.finding_investors.investment_types.forEach(
      (typeKey: string) => {
        const mapping = investmentTypesMapping.find((m) => m.key === typeKey);
        if (mapping) {
          types.push(mapping.label);
        }
      },
    );

    return types;
  }

  getLoanTypesArray(): string[] {
    if (!this.savedCriteria?.criteria?.loans_and_capital?.loan_types) return [];

    const loanTypesKeys =
      this.savedCriteria.criteria.loans_and_capital.loan_types;

    // Convert API keys to display labels
    return loanTypesKeys
      .map((key: string) => this.loanTypeReverseMapping[key])
      .filter((label: string) => label !== undefined);
  }

  getFinancialDocumentationsArray(): string[] {
    if (
      !this.savedCriteria?.criteria?.loans_and_capital?.financial_documentations
    )
      return [];

    const financialDocsMapping = [
      { key: 'audited_statements', label: 'Audited financial statements' },
      {
        key: 'unaudited_statements',
        label: 'Unaudited formal financial statements',
      },
      { key: 'informal_statements', label: 'Informal financial statements' },
      { key: 'tax_returns', label: 'Tax returns' },
      { key: 'no_statements', label: 'No financial statements available' },
      { key: 'bank_statements', label: 'Bank Statements' },
    ];

    const docs: string[] = [];
    this.savedCriteria.criteria.loans_and_capital.financial_documentations.forEach(
      (docKey: string) => {
        const mapping = financialDocsMapping.find((m) => m.key === docKey);
        if (mapping) {
          docs.push(mapping.label);
        }
      },
    );

    return docs;
  }

  // Utility methods for view mode
  getDisplayValue(arr: string[]): string {
    return arr && arr.length > 0 ? arr.join(', ') : 'Not specified';
  }

  getDisplayValueFromCriteria(field: keyof LeadsCriteriaValues): string {
    if (!this.savedCriteria?.criteria) return 'Not specified';
    const value = this.savedCriteria.criteria[field];
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Not specified';
    }
    return 'Not specified';
  }

  private clearInvalidCounties(): void {
    const countiesArray = this.countiesOfServiceArray;
    for (let i = countiesArray.length - 1; i >= 0; i--) {
      const county = countiesArray.at(i).value;
      if (!this.availableCounties.includes(county)) {
        countiesArray.removeAt(i);
      }
    }
  }

  onGoBack(): void {
    // Check if we're in edit mode and the form has been modified
    if (this.isEditMode && this.hasFormChanges()) {
      this.showBackConfirmPopup = true;
    } else {
      this.navigateBack();
    }
  }

  onBackConfirmed(): void {
    this.showBackConfirmPopup = false;
    this.navigateBack();
  }

  onBackCancelled(): void {
    this.showBackConfirmPopup = false;
  }

  private navigateBack(): void {
    this.router.navigate(['/deeproots/leads-criteria']);
  }

  private hasFormChanges(): boolean {
    // Check if form is dirty (has been modified)
    return this.matchingCriteriaForm?.dirty || false;
  }

  // Mandatory match field methods
  toggleMandatoryField(fieldName: string): void {
    if (this.mandatoryMatchFields.has(fieldName)) {
      this.mandatoryMatchFields.delete(fieldName);
    } else {
      // Only allow adding to mandatory match if field has values
      if (this.hasFieldValue(fieldName)) {
        this.mandatoryMatchFields.add(fieldName);
      }
    }
    this.matchingCriteriaForm.markAsDirty();
  }

  isMandatoryField(fieldName: string): boolean {
    return this.mandatoryMatchFields.has(fieldName);
  }

  // Check if a field has any values
  hasFieldValue(fieldName: string): boolean {
    // Handle nested field names (e.g., 'loans_and_capital.need_state_registration')
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      const section = parts[0];

      // Check if the parent business support option is selected
      const businessSupport = this.businessSupportArray.value;

      if (
        !businessSupport.includes(BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME[section])
      ) {
        return false;
      }

      // Map the nested field names to their form control names
      const nestedFieldMapping: Record<string, string> = {
        // Loans and Capital
        'loans_and_capital.need_state_registration': 'needStateRegistration',
        'loans_and_capital.cosigner_helps': 'cosignerHelps',
        'loans_and_capital.need_business_plan': 'needBusinessPlan',
        'loans_and_capital.need_financial_projections':
          'needFinancialProjections',
        'loans_and_capital.loan_types': 'loanTypes',
        'loans_and_capital.loan_size_range': 'loanMinAmount,loanMaxAmount',
        'loans_and_capital.financial_documentations': 'financialDocumentation',
        'loans_and_capital.credit_score_range': 'creditScoreRange',
        'loans_and_capital.capital_deployment_areas': 'areaTypes',
        // Technical Assistance
        'technical_assistance.target_industries': 'targetIndustries',
        'technical_assistance.support_areas': 'supportAreas',
        'technical_assistance.offers_employee_ownership':
          'offersEmployeeOwnership',
        'technical_assistance.business_help_areas': 'businessHelpAreas',
        'technical_assistance.ta_programs_paid_service':
          'taProgramsPaidService',
        'technical_assistance.paid_support_areas': 'paidSupportAreas',
        // Business Operational Tools
        'business_operational_tools.tools': 'operationalTools',
        // Commercial Real Estate
        'commercial_real_estate.cre_help_locate_facilities':
          'creHelpLocateFacilities',
        'commercial_real_estate.cre_upgrade_facility': 'creUpgradeFacility',
        'commercial_real_estate.cre_need_own_facility': 'creNeedOwnFacility',
        'commercial_real_estate.cre_work_with_tenants': 'creWorkWithTenants',
        'commercial_real_estate.cre_solar_energy_upgrades':
          'creSolarEnergyUpgrades',
        // Solar and Energy
        'solar_and_energy.solar_lending_products': 'solarLendingProducts',
        'solar_and_energy.solar_loan_amount_range':
          'solarLoanMinAmount,solarLoanMaxAmount',
        'solar_and_energy.solar_need_credit_score': 'solarNeedCreditScore',
        'solar_and_energy.solar_credit_score_ranges': 'solarCreditScoreRange',
        'solar_and_energy.offers_energy_efficiency': 'offersEnergyEfficiency',
        'solar_and_energy.offers_battery_lending': 'offersBatteryLending',
        'solar_and_energy.battery_loan_amount_range':
          'batteryLoanMinAmount,batteryLoanMaxAmount',
        'solar_and_energy.battery_need_credit_score': 'batteryNeedCreditScore',
        'solar_and_energy.battery_credit_score_ranges':
          'batteryCreditScoreRange',
        'solar_and_energy.need_power_use_statement': 'needPowerUseStatement',
        'solar_and_energy.offers_ev_products': 'offersEvProducts',
        'solar_and_energy.ev_loan_amount_range':
          'evLoanMinAmount,evLoanMaxAmount',
        'solar_and_energy.ev_need_credit_score': 'evNeedCreditScore',
        'solar_and_energy.ev_credit_score_ranges': 'evCreditScoreRange',
        // Finding Investors
        'finding_investors.investment_types': 'investmentTypes',
        'finding_investors.require_regional_investment':
          'requireRegionalInvestment',
      };

      const formFieldName = nestedFieldMapping[fieldName];
      if (formFieldName) {
        // Handle special case for range fields with two controls
        if (formFieldName.includes(',')) {
          const [minField, maxField] = formFieldName.split(',');
          const minControl = this.matchingCriteriaForm.get(minField);
          const maxControl = this.matchingCriteriaForm.get(maxField);
          return (
            !!minControl?.value &&
            minControl.value > 0 &&
            !!maxControl?.value &&
            maxControl.value > 0
          );
        }

        const control = this.matchingCriteriaForm.get(formFieldName);
        if (control instanceof FormArray) {
          return control.length > 0;
        }
        return !!control?.value && control.value !== '';
      }
      return false;
    }

    // Map field names to form array names
    const fieldMapping: Record<string, string> = {
      preferred_languages: 'supportedLanguages',
      states: 'statesOfService',
      counties: 'countiesOfService',
      cities: 'cities',
      zip_codes: 'zipCodes',
      supports: 'businessSupport',
      is_military_veteran_prog: 'isMilitaryProgram',
      is_veteran_spouse_prog: 'isVeteranSpouse',
      need_business_entity: 'needBusinessEntity',
    };

    const formFieldName = fieldMapping[fieldName] || fieldName;
    const control = this.matchingCriteriaForm.get(formFieldName);

    if (control instanceof FormArray) {
      // For arrays, check if there are any non-empty values
      return (
        control.length > 0 &&
        control.value.some((val: string) => val && val.trim() !== '')
      );
    }

    // For regular form controls (like radio buttons)
    return !!control?.value && control.value !== '';
  }

  // Check and auto-uncheck mandatory match if field becomes empty
  private checkAndClearMandatoryMatch(fieldName: string): void {
    if (this.mandatoryMatchFields.has(fieldName)) {
      if (!this.hasFieldValue(fieldName)) {
        this.mandatoryMatchFields.delete(fieldName);
      }
    }
  }

  // Check if must match checkbox should be disabled
  isMustMatchDisabled(fieldName: string): boolean {
    return !this.hasFieldValue(fieldName);
  }

  formatCityName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  @HostListener('wheel', ['$event'])
  onWheelOverNumberInput(event: WheelEvent): void {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.type !== 'number') {
      return;
    }

    if (document.activeElement === target) {
      target.blur();
    }
  }
}
